// MAPA DE CONECTIVIDAD ECOLÓGICA DE PANAMÁ
// Fundación Almanaque Azul, marzo 2023
// Por Mir Rodríguez Lombardo y Michelle Szejner
// Cartografía por Mir

// OpenLayers
import Map from "ol/Map.js";
import View from "ol/View.js";
import Projection from "ol/proj/Projection.js";
import TileLayer from "ol/layer/Tile.js";
import VectorLayer from "ol/layer/Vector";
import VectorTile from "ol/layer/VectorTile";
import WebGLTile from "ol/layer/WebGLTile.js";
import Graticule from "ol/layer/Graticule";
import { getCenter } from "ol/extent.js";
import { ScaleLine, defaults as defaultControls } from "ol/control.js";
import LayerSwitcher from "ol-ext/control/LayerSwitcher.js";
import DragOverlay from "ol-ext/interaction/DragOverlay.js";
import { Group as LayerGroup } from "ol/layer.js";

import VectorSource from "ol/source/Vector";
import VectorTileSource from "ol/source/VectorTile";
import GeoTIFF from "ol/source/GeoTIFF.js";
import XYZ from "ol/source/XYZ.js";
import OSM from "ol/source/OSM";
import { PMTilesVectorSource } from "ol-pmtiles";
import { PMTilesRasterSource } from "ol-pmtiles";

import GPX from "ol/format/GPX";
import IGC from "ol/format/IGC";
import MVT from "ol/format/MVT";
import TopoJSON from "ol/format/TopoJSON.js";
import GeoJSON from "ol/format/GeoJSON";
//import KML from "ol/format/KML";
// JSZip
// import JSZip from "jszip";

import * as tileGrid from "ol/tilegrid.js";
import * as loadingstrategy from "ol/loadingstrategy";

import colormap from "colormap";

import geoblazeParse from "geoblaze/lite/parse.js";
import geoblazeHistogram from "geoblaze/lite/histogram.js";

// noUiSlider and wNumb
import noUiSlider from "nouislider";
import wNumb from "wnumb";

// OpenLayers Styles
import {
  Icon,
  Circle as CircleStyle,
  RegularShape,
  Text,
  Fill,
  Stroke,
  Style,
} from "ol/style.js";

// OpenLayers Interactions
import Draw from "ol/interaction/Draw.js";
import {
  DragAndDrop,
  DragRotateAndZoom,
  defaults as defaultInteractions,
} from "ol/interaction";

// OpenLayers Overlays
import Overlay from "ol/Overlay.js";

// OpenLayers Geometries and Spheres
import { LineString, Polygon } from "ol/geom.js";
import { getArea, getLength } from "ol/sphere.js";

// OpenLayers Utilities
import { unByKey } from "ol/Observable.js";

// OpenLayers Controls
import Attribution from "ol/control/Attribution";

// iwanthue
import iwanthue from "iwanthue";

// Posibles valores son: "arcgis", "geoserver" y "pmtiles"
const fuentes = {
  capaRíosGrandes: "pmtiles",
  capaRíosMedianos: "pmtiles",
  capaRíosChicos: "pmtiles",
  capaProvincias: "pmtiles",
};

// ****** MÍNIMOS Y MÁXIMOS INICIALES PARA CAPAS DE CONECTIVIDAD ******

const conectMin6k = 30;
const conectMax6k = 100;
const conectMin35k = 30;
const conectMax35k = 100;

// ****** COLORES PARA CAPAS DE CONECTIVIDAD (COLOR 1 VALORES BAJOS, COLOR 2 VALORES ALTOS) ******

const color1_6k = [127, 0, 254];
const color2_6k = [236, 52, 254];
const color1_35k = [84, 81, 233];
const color2_35k = [130, 130, 233];

// *** ELEMENTOS DEL DOM ***

const contenedorMapa = document.getElementById("contenedorMapa");
const contenedorCapas = document.getElementById("contenedorCapas");
const contenedorPrincipalSlider = document.getElementById(
  "contenedorPrincipalSlider"
);
const sliderHorizontal = document.getElementById("sliderHorizontal");
const sliderAñoPérdida = document.getElementById("sliderAñoPérdida");
const pérdidaArbórea = document.getElementById("pérdidaArbórea");
const botónPérdidaArbórea = document.getElementById("botónPérdidaArbórea");
const cursor6k = document.getElementById("cursor6k");

const infoBox = document.getElementById("infoBox");
const botonesColor = document.querySelectorAll("input[name='opcionColor']");
const acercaDe = document.getElementById("acercaDe");
const infoMapa = document.getElementById("infoMapa");
const cerrarModal = document.getElementsByClassName("cerrarModal")[1];

// const leyenda = document.getElementById("leyenda");

const svgMedirDist = document.querySelector(".svg-medir-distancia");
const botonMedirDist = document.querySelector(".boton-medir-distancia");

const svgMedirArea = document.querySelector(".svg-medir-area");
const botonMedirArea = document.querySelector(".boton-medir-area");
const toggleBtn = document.querySelector(".js-boton-pantalla-completa");

// Agarrar parámetros del URL
const queryString = window.location.search;
const urlParams = new URLSearchParams(queryString);
let capaExtra = false;

let vh = Math.max(
  document.documentElement.clientHeight || 0,
  window.innerHeight || 0
);

let vw = Math.max(
  document.documentElement.clientWidth || 0,
  window.innerWidth || 0
);

// Por algún motivo estos botones comienzan con los valores equivocados y los tengo que volver a asignar aquí

botonesColor[0].checked = true;

// *** ALGUNOS ESTILOS ***

const analisisCats = {
  MEDIABAJA: "#e0c504",
  MEDIANA: "#a1d303",
  ALTA: "#345a01",
};

const mineriaTipo = {
  "Solicitud metálica": ["#ffc50190", "#434343"],
  "Reserva minera": ["#dd1ba090", "#434343"],
  "Concesión de extracción": ["#ff2701dd", "#434343"],
  Rechazadas: ["#74db1f90", "#434343"],
  Caducidad: ["#96969690", "#434343"],
  "Concesión de exploración": ["#ff4d0190", "#434343"],
  else: ["#cfb54c90", "#ff2701dd"],
};

// Esta variable define el estilo para los ríos
// con base en el órden Scheidegger: scheidegger min, scheidegger max, ancho en metros

const estiloRiosScheidegger = [
  [2, 10 ** 0.5, 20],
  [10 ** 0.5, 10 ** 1, 40],
  [10 ** 1, 10 ** 1.5, 60],
  [10 ** 1.5, 10 ** 2, 80],
  [10 ** 2, 10 ** 2.5, 100],
  [10 ** 2.5, 10 ** 3, 120],
  [10 ** 3, 10 ** 3.5, 140],
  [10 ** 3.5, 10 ** 4, 160],
  [10 ** 4, 10 ** 4.5, 180],
  [10 ** 4.5, 10 ** 5, 200],
];

const resolutions = [
  156543.03392804076, 78271.51696402022, 39135.75848201011, 19567.879241005117,
  9783.939620502542, 4891.969810251271, 2445.9849051256383, 1222.9924525628178,
  611.4962262814075, 305.7481131407024, 152.8740565703523, 76.43702828517615,
  38.21851414258808, 19.10925707129404, 9.55462853564699, 4.777314267823495,
  2.388657133911756, 1.1943285669558765, 0.5971642834779383,
  0.29858214173896913, 0.14929107086948457, 0.07464553543474216,
  0.03732276771737108, 0.018661383858685567, 0.009330691929342784,
];
const gridNames = [
  "0",
  "1",
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
  "10",
  "11",
  "12",
  "13",
  "14",
  "15",
  "16",
  "17",
  "18",
  "19",
  "20",
  "21",
  "22",
  "23",
  "24",
];

const projection = new Projection({
  code: "EPSG:3857",
  units: "m",
  extent: [-20037508.34, -20048966.1, 20037508.34, 20048966.1],
  worldExtent: [-83.5, 5, -76, 10.5],
});

// var zip = new JSZip();

var infoBoxContent = "";

var capasDetectadas = {};
var selectedFeature = null;
var coloresAleatorios = [];
var maxRangoAños = [];
var selectedLayer = null;

// **** EXTENT ****

// const sourceExtent = [264100, 780585, 933520, 1081905]; // EPSG 32617
//const sourceExtent = [-9256276, 786265, -8577183, 1094864]; // EPSG 3857
const sourceExtent = [-9258746, 736265, -8561894, 1140657]; // EPSG 3857, ajustado por el uso del constrainResolution

// ***************
// **** CAPAS ****
// ***************

// *** Capa de vector de medición de longitud o área ***

const fuenteMedir = new VectorSource();

const capaMedir = new VectorLayer({
  displayInLayerSwitcher: false,
  source: fuenteMedir,
  style: {
    "fill-color": "rgba(255, 255, 255, 0.2)",
    "stroke-color": "#ffcc33",
    "stroke-width": 2,
    "circle-radius": 7,
    "circle-fill-color": "#ffcc33",
  },
});

var actualCapaPérdida = false;
var estadoCapasPérdida = [];

// // *** CONECTIVIDAD ***

// const conectividad = ["band", 1];

// const fuenteConectividad6k = new GeoTIFF({
//   sources: [
//     {
//       url: "./data/panama_currmap_6k_cog_3857.tif",
//     },
//   ],
//   normalize: false,
// });

// const capaConectividad6k = new WebGLTile({
//   className: "capaConectividad",
//   title: "Conectividad 6km",
//   slider: sliderAñoPérdida,
//   source: fuenteConectividad6k,
//   tipo: "conectividad",
//   visible: true,
// });

// const fuenteConectividad35k = new GeoTIFF({
//   sources: [
//     {
//       url: "./data/panama_currmap_35k_cog_3857.tif",
//     },
//   ],
//   normalize: false,
// });

// const capaConectividad35k = new WebGLTile({
//   className: "capaConectividad",
//   title: "Conectividad 35km",
//   // slider: slider35k,
//   source: fuenteConectividad35k,
//   visible: false,
//   tipo: "conectividad",
// });

// function obtenerValoresCapaBase() {
//   var valores = {};
//   mapa.getLayers().forEach(function (capa) {
//     if (capa instanceof LayerGroup) {
//       capa.getLayers().forEach(function (subcapa) {
//         if (capa instanceof LayerGroup) {
//           if (subcapa.values_.baseLayer) {
//             if (subcapa.getVisible()) {
//               valores = subcapa.values_;
//             }
//           }
//         }
//       });
//     }
//   });

//   return valores;
// }

// *** AMENAZAS ***

// MINERIA E HIDRO

const iconoMinería = new Style({
  image: new Icon({
    src: "./data/iconos/mineria.svg",
    scale: 0.05,
  }),
});

const iconoHidro = new Style({
  image: new Icon({
    src: "./data/iconos/hidroelectrica.svg",
    scale: 0.03,
  }),
});

const iconoTermo = new Style({
  image: new Icon({
    src: "./data/iconos/termoelectrica.svg",
    scale: 0.03,
  }),
});

const iconoEólica = new Style({
  image: new Icon({
    src: "./data/iconos/eolica.svg",
    scale: 0.03,
  }),
});

const estilosAmenazas = {
  minería: iconoMinería,
  generación: {
    hidroeléctrica: iconoHidro,
    eólica: iconoEólica,
    "térmica fósil": iconoTermo,
  },
};

const capaMinaHidro = new VectorLayer({
  name: "Generación de electricidad",
  tipo: "amenazas",
  source: new VectorSource({
    format: new GeoJSON(),
    url: "./data/infraestructura.geojson",
  }),
  style: function (feature, resolution) {
    const tipo = feature.get("tipo");
    const subtipo = feature.get("subtipo");

    if (tipo == "minería") {
      return estilosAmenazas[tipo];
    } else {
      return estilosAmenazas[tipo][subtipo];
    }
  },
});

const fuenteMineria = new PMTilesVectorSource({
  url: "./data/mineria.pmtiles",
});

//const capaMineria = new VectorLayer({
const capaMineria = new VectorTile({
  name: "Minería",
  className: "capaConectividad",
  blendMode: "multiply",

  displayInLayerSwitcher: true,
  source: fuenteMineria,
  visible: false,
  zIndex: 2000,

  style: estiloPolyMineria,
  opacity: 1,
});

// TRANSMISIÓN ELECTRICA

const transmisionFuente = new VectorSource({
  format: new GeoJSON(),
  url: "./data/transmision_electrica.geojson",
});

const capaTransmisiónEléctrica = new VectorLayer({
  name: "Líneas de transmisión",
  tipo: "amenazas",
  //displayInLayerSwitcher: false,
  source: transmisionFuente,

  extent: [-9532166, 848431, -8403468, 1253867],
  style: estiloTransmisiónEléctrica,
});

const capaInfra = new LayerGroup({
  title: "Infraestructura y amenazas",
  layers: [capaMinaHidro, capaTransmisiónEléctrica],
  visible: false,
});

// *** REFERENCIA ***

// const territoriosWMSSource = new TileWMS({
//   url: "https://geo.almanaqueazul.org/geoserver/territoriosraster/wms",
//   params: {
//     LAYERS: "territoriosraster:territorios_indigenas",
//   },
// });

// const capaTerritorios = new TileLayer({
//   name: "Territorios indígenas",
//   displayInLayerSwitcher: true,
//   opacity: 0.6,
//   source: new WMTS({
//     url: "https://geo.almanaqueazul.org/geoserver/gwc/service/wmts",
//     layer: "territoriosraster:territorios_indigenas",
//     matrixSet: "WebMercatorQuad",
//     format: "image/png",
//     projection: projection,
//     tileGrid: new WMTSTileGrid({
//       tileSize: [256, 256],
//       extent: [
//         -2.003750834e7, -2.0037508345578436e7, 2.0037508345578436e7,
//         2.003750834e7,
//       ],
//       origin: [-2.003750834e7, 2.003750834e7],
//       resolutions: resolutions,
//       matrixIds: gridNames,
//     }),
//   }),
// });

// SINAP

// const urlSINAP =
//   "https://services2.arcgis.com/HRY6x8qt5qjGnAA9/ArcGIS/rest/services/Panama_Protected_National_Parks/FeatureServer/0";

// const fuenteSINAP = new VectorSource({
//   format: new EsriJSON(),
//   url: function (extent, resolution, projection) {
//     // ArcGIS Server only wants the numeric portion of the projection ID.
//     const srid = projection
//       .getCode()
//       .split(/:(?=\d+$)/)
//       .pop();
//     const url =
//       urlSINAP +
//       "/query/?f=json&" +
//       "returnGeometry=true&spatialRel=esriSpatialRelIntersects&geometry=" +
//       encodeURIComponent(
//         '{"xmin":' +
//           extent[0] +
//           ',"ymin":' +
//           extent[1] +
//           ',"xmax":' +
//           extent[2] +
//           ',"ymax":' +
//           extent[3] +
//           ',"spatialReference":{"wkid":' +
//           srid +
//           "}}"
//       ) +
//       "&geometryType=esriGeometryEnvelope&inSR=" +
//       srid +
//       "&outFields=*" +
//       "&outSR=" +
//       srid;

//     return url;
//   },
//   strategy: tileStrategy(
//     createXYZ({
//       tileSize: 512,
//     })
//   ),
//   attributions: "Miambiente/STRI",
// });

const fuenteSINAP = new PMTilesVectorSource({
  url: "./data/SINAP.pmtiles",
});

//const capaSINAP = new VectorLayer({
const capaSINAP = new VectorTile({
  name: "Áreas protegidas",
  displayInLayerSwitcher: true,
  source: fuenteSINAP,
  visible: true,

  style: new Style({
    stroke: new Stroke({
      // color: "#adcff3",
      color: "#000",
      lineCap: "round",
      lineDash: [0.5, 5],
      width: 2.5,
    }),
    fill: new Fill({
      color: "#a2e6a733",
    }),
  }),
  opacity: 1,
});

// PÉRDIDA ARBÓREA

const fuenteHansen = new GeoTIFF({
  sources: [
    {
      url: "./data/Hansen_GFC-2023-v1.11_lossyear_panama_COG.tif",
      min: 2001,
      max: 2023,
      minInicial: 2001,
      maxInicial: 2022,
      ajuste: -2000,
      apodo: "Hansen",
    },
  ],
  normalize: false,
  interpolate: false,
  attributions_: "Hansen/UMD/Google/USGS/NASA",
});

const capaHansen = new WebGLTile({
  name: "Deforestación Global Forest Watch",
  displayInLayerSwitcher: true,
  className: "capaHansen",
  title: "Deforestación Global Forest Watch",
  source: fuenteHansen,
  tipo: "pérdida",
  visible: true,
  opacity: 0.8,
});

const fuenteTMF = new GeoTIFF({
  sources: [
    {
      url: "./data/JRC_TMF_DeforestationYear_INT_1982_2022_COG.tif",
      min: 1983,
      max: 2022,
      minInicial: 2001,
      maxInicial: 2022,
      apodo: "TMF",
    },
  ],
  normalize: false,
  interpolate: false,
  attributions: "Source: EC JRC",
});

const capaTMF = new WebGLTile({
  name: "Deforestación Comisión Europea",
  displayInLayerSwitcher: true,
  className: "capaTMF",
  title: "Deforestación Comisión Europea",
  source: fuenteTMF,
  tipo: "pérdida",
  visible: false,
  opacity: 0.8,
});

var coloresTMF = [
  "interpolate",
  ["linear"],
  ["band", 1],
  0,

  "#1a4dda00",
  2000,
  "#1a4dda00",

  2001,

  "#ff2c19ff",
  2022,
  "#ff2c19ff",
  2023,
  "#1a4dda00",
];

var estiloTMF = {
  color: coloresTMF,
};

// Corredores naturales

const capaCorredoresNaturales = new VectorLayer({
  className: "capaConectividad",
  name: "Corredores naturales",
  source: new VectorSource({
    format: new GeoJSON(),
    url: "./data/interpretacion_conectividad_5_3857.geojson",
  }),
  style: estiloPolyInterpretacion,
  blendMode: "multiply",
  opacity: 0.9,
  displayInLayerSwitcher: true,
  visible: false,
});

function estiloPolyInterpretacion(feature, resolution) {
  const cat = feature.get("cat").toUpperCase();
  return new Style({
    fill: new Fill({
      color: analisisCats[cat],
    }),
    stroke: new Stroke({
      color: [255, 255, 255, 0],
      width: 0,
    }),
  });
}

// CERROS

// Ancho, relleno y pluma base para pueblos y cerros

const fill = new Fill({
  color: "rgba(255,255,255,0)",
});
const stroke = new Stroke({
  color: "#fff",
  width: 2,
});
const width = 5;

const triángulo = new Style({
  image: new RegularShape({
    fill: fill,
    stroke: new Stroke({
      color: "#fff",
      width: 1,
    }),
    points: 3,
    radius: width,
    angle: 0,
  }),
});

const estiloCerrosNombre = new Style({
  text: new Text({
    font: "0.8em Delicious-sc",
    overflow: true,
    fill: new Fill({
      color: "#fff",
    }),

    textAlign: "left",
    justify: "left",
    offsetX: width + 8,
    offsetY: -width - 6,
  }),
});

const estiloCerrosAltura = new Style({
  text: new Text({
    font: "0.8em Delicious-sc",
    overflow: true,
    fill: new Fill({
      color: "#fff",
    }),

    textAlign: "left",
    justify: "left",

    offsetX: width + 6,
    offsetY: width + 6,
  }),
});

const capaCerros = new VectorLayer({
  name: "Cerros",
  displayInLayerSwitcher: true,
  source: new VectorSource({
    format: new GeoJSON(),
    url: "./data/cerros.geojson",
  }),
  zIndex: 1000,
  style: function (feature) {
    const nombre = feature.get("nombre");
    const nombreCerro = nombre ? nombre.replace("Cerro ", "") : "";
    const altura = feature.get("ele") + " m";

    estiloCerrosNombre.getText().setText(nombreCerro);
    estiloCerrosAltura.getText().setText(altura);

    return [triángulo, estiloCerrosNombre, estiloCerrosAltura];
  },
  declutter: true,
});

const capaReferencia = new LayerGroup({
  title: "Cerros",
  //layers: [capaTerritorios, capaCerros],
  layers: [capaCerros],
  visible: false,
});

// *** COBERTURA BOSCOSA ***

const fuenteCobertura = new GeoTIFF({
  sources: [
    {
      url: "./data/miambiente_bosque_estricto_3857_cog.tif",
    },
  ],
  normalize: false,
});

const capaCobertura = new WebGLTile({
  className: "capaConectividad",
  title: "Bosques Miambiente 2021",
  source: fuenteCobertura,
  tipo: "cobertura",
  visible: false,
  opacity: 0.5,
});
var coloresCobertura = [
  "match",
  ["band", 1],
  1,
  "#005800",
  2,
  "#00a600",
  3,
  "#21a74d",
  4,
  "#007544",
  5,
  "#007544",
  6,
  "#007544",
  [255, 255, 255, 0],
];

var estiloCobertura = {
  color: coloresCobertura,
};
capaCobertura.setStyle(estiloCobertura);

// *** CAPAS DEFINIDAS POR EL URL ***

if (urlParams.get("capa")) {
  const nombreCapaExtra = urlParams.get("capa");
  var url = "./data/" + nombreCapaExtra + ".geojson";

  capaExtra = new VectorLayer({
    name: "Corredor Pro Eco Azuero",
    opacity: 0.6,
    source: new VectorSource({
      format: new GeoJSON(),
      url: "./data/" + nombreCapaExtra + ".geojson",
    }),
    style: new Style({
      fill: new Fill({
        color: "#4daf4add",
      }),
      stroke: new Stroke({
        color: [255, 255, 255, 20],
        width: 1,
      }),
    }),
  });
}

// *** INTERPRETACIÓN ***

// const estiloOjo = new Style({
//   image: new Icon({
//     src: "./data/iconos/Eye_open_font_awesome.svg",
//     scale: 0.4,
//   }),
// });

// const capaPolyIterpretación = new VectorLayer({
//   className: "capaPolyIterpretación",
//   name: "Corredor",
//   source: new VectorSource({
//     format: new GeoJSON(),
//     url: "./data/interpretacion_conectividad_5_3857.geojson",
//   }),
//   style: estiloPolyInterpretacion,
//   blendMode: "normal",
// });

// const capaOjo = new VectorLayer({
//   name: "Ojo",
//   source: new VectorSource({
//     format: new GeoJSON(),
//     url: "./data/ojos_3_3857.geojson",
//   }),
//   zIndex: 1000,
//   style: estiloOjo,
//   declutter: true,
// });

// const capaInterpretación = new LayerGroup({
//   title: "Corredores y ojos",
//   layers: [capaPolyIterpretación, capaOjo],
//   visible: false,
// });

// *** CAPA BASE AA ***

//  FEATURES

const estiloFeaturesLineal = new Style({
  stroke: new Stroke({
    color: "rgba(255,255,255,0)",
    width: 2,
  }),
  text: new Text({
    fill: new Fill({
      color: "#fff",
    }),
    overflow: true,
    placement: "line",
    baseline: "middle",
  }),
});

const capaFeaturesLineal = new VectorLayer({
  name: "Features lineas",
  displayInLayerSwitcher: false,
  source: new VectorSource({
    format: new GeoJSON(),
    url: "./data/features_lineas.geojson",
  }),
  zIndex: 1000,
  style: featureTextoEstilo,
  declutter: true,
});

//  PUEBLOS

const estiloPueblosGrandes = new Style({
  image: new CircleStyle({
    fill: fill,
    stroke: new Stroke({
      color: "#fff",
      width: 2,
    }),
    radius: width - 1,
  }),
  fill: fill,
  stroke: stroke,
  text: new Text({
    font: "1em Delicious-roman",
    overflow: true,
    fill: new Fill({
      color: "#fff",
    }),
    stroke: new Stroke({
      color: "rgba(0,0,0,0.6)",
      width: 3,
    }),
    textAlign: "left",
    offsetX: width + 8,
    offsetY: width + 6,
  }),
});

const estiloPueblosGrandesCroquis = new Style({
  image: new CircleStyle({
    fill: fill,
    stroke: new Stroke({
      color: "#1a854a",
      width: 2,
    }),
    radius: width - 1,
  }),
  fill: fill,
  stroke: stroke,
  text: new Text({
    font: "1em Delicious-roman",
    overflow: true,
    fill: new Fill({
      color: "#1a854a",
    }),

    textAlign: "left",
    offsetX: width + 8,
    offsetY: width + 6,
  }),
});

const estiloPueblosChicosMedianos = new Style({
  image: new CircleStyle({
    fill: fill,
    stroke: stroke,
    radius: width - 2,
  }),
  fill: fill,
  stroke: stroke,
  text: new Text({
    font: "0.9em Delicious-roman",
    overflow: true,
    fill: new Fill({
      color: "#fff",
    }),
    stroke: new Stroke({
      color: "rgba(0,0,0,0.4)",
      width: 3,
    }),
    textAlign: "left",
    offsetX: width + 7,
    offsetY: width + 7,
  }),
});

const estiloPueblosChicosMedianosCroquis = new Style({
  image: new CircleStyle({
    fill: fill,
    stroke: new Stroke({
      color: "#1a854a",
      width: 2,
    }),
    radius: width - 1,
  }),
  fill: fill,
  stroke: stroke,
  text: new Text({
    font: "0.75em Delicious-roman",
    overflow: true,
    fill: new Fill({
      color: "#1a854a",
    }),

    textAlign: "left",
    offsetX: width + 8,
    offsetY: width + 6,
  }),
});
const fuentePueblosGrandes = new VectorSource({
  format: new GeoJSON(),
  url: "./data/pueblos_grandes.geojson",
});

const capaPueblosGrandes = new VectorLayer({
  name: "Pueblos 1",
  displayInLayerSwitcher: false,
  source: fuentePueblosGrandes,
  zIndex: 1000,
  style: function (feature) {
    const label = feature.get("NOMB_MOST").replace("\\", "\n");
    estiloPueblosGrandes.getText().setText(label);
    return [estiloPueblosGrandes];
  },
  declutter: true,
});

const capaPueblosMedianos = new VectorLayer({
  name: "Pueblos 2",
  displayInLayerSwitcher: false,
  source: new VectorSource({
    format: new GeoJSON(),
    url: "./data/pueblos_2.geojson",
  }),
  zIndex: 1000,
  minZoom: 10,
  style: function (feature) {
    const label = feature.get("NOMB_MOST").replace("\\", "\n");
    estiloPueblosChicosMedianos.getText().setText(label);

    return [estiloPueblosChicosMedianos];
  },
  declutter: true,
});

const capaPueblosChicos = new VectorLayer({
  name: "Pueblos 3",
  displayInLayerSwitcher: false,
  source: new VectorSource({
    format: new GeoJSON(),
    url: "./data/pueblos_3.geojson",
  }),
  zIndex: 1000,

  minZoom: 12,
  style: function (feature) {
    const label = feature.get("NOMB_MOST");
    if (label) {
      estiloPueblosChicosMedianos.getText().setText(label.replace("\\", "\n"));
    }
    return [estiloPueblosChicosMedianos];
  },
  declutter: true,
});

//  CARRETERAS

// const panamericanaFuente = new VectorTileSource({
//   projection: "EPSG:3857",
//   tilePixelRatio: 1, // oversampling when > 1
//   tileGrid: tileGrid.createXYZ({ maxZoom: 16 }),
//   format: new MVT(),
//   url: "https://geo.almanaqueazul.org/geoserver/gwc/service/tms/1.0.0/conectividad:panamericana@WebMercatorQuad@pbf/{z}/{x}/{-y}.pbf",
// });

const panamericanaFuente = new PMTilesVectorSource({
  url: "./data/panamericana_AA.pmtiles",
});

const panamericanaLayer = new VectorTile({
  name: "Panamericana",
  tipo: "calles",
  displayInLayerSwitcher: false,
  source: panamericanaFuente,
  extent: [-9221962, 900069, -8647798, 1032369],
  style: function (feature, resolution) {
    const carriles = parseFloat(feature.get("carriles"));

    var ancho = (carriles * 20) / resolution;

    return [
      new Style({
        stroke: new Stroke({
          color: "#606060",
          width: ancho,
          lineCap: "butt",
        }),
      }),
      new Style({
        stroke: new Stroke({
          color: "#dadada",
          width: ancho / 2,
        }),
      }),
    ];
  },
});

//  RIOS

// Ríos grandes
// Common configurations
const parámetrosComunesRíos = {
  tipo: "ríos",
  displayInLayerSwitcher: false,
  extent: [-9256276, 786265, -8577183, 1094864],
  style: function (feature, resolution) {
    return anchoRios(feature, "scheidegger", resolution);
  },
};

// Helper function to create VectorTileSource
function crearFuenteVectorTiles(url) {
  return new VectorTileSource({
    projection: "EPSG:3857",
    tilePixelRatio: 1, // oversampling when > 1
    tileGrid: tileGrid.createXYZ({ maxZoom: 16 }),
    format: new MVT(),
    url: url,
  });
}

// Helper function to create VectorSource
function crearFuenteVectores(url) {
  return new VectorSource({
    format: new GeoJSON({ dataProjection: "EPSG:3857" }),
    url: function (extent) {
      return URLArcGISWFS(extent, url);
    },
    strategy: loadingstrategy.tile(tileGrid.createXYZ({ maxZoom: 16 })),
  });
}

// Layers configuration
const capaRíosGrandes = (() => {
  var parámetrosComunesRíosGrandes = {
    ...parámetrosComunesRíos,
    name: "Ríos grandes",
  };
  switch (fuentes.capaRíosGrandes) {
    case "geoserver":
      return new VectorTile({
        ...parámetrosComunesRíosGrandes,
        source: crearFuenteVectorTiles(
          "https://geo.almanaqueazul.org/geoserver/gwc/service/tms/1.0.0/conectividad:rios_grandes@WebMercatorQuad@pbf/{z}/{x}/{-y}.pbf"
        ),
      });
    case "arcgis":
      return new VectorLayer({
        ...parámetrosComunesRíosGrandes,
        source: crearFuenteVectores("rios_grandes"),
      });
    case "pmtiles":
      return new VectorTile({
        ...parámetrosComunesRíosGrandes,
        source: new PMTilesVectorSource({
          url: "./data/rios_grandes.pmtiles",
        }),
      });
  }
})();

const capaRíosMedianos = (() => {
  var parámetrosComunesRíosMedianos = {
    ...parámetrosComunesRíos,
    name: "Ríos medianos",
    minZoom: 11,
  };
  switch (fuentes.capaRíosMedianos) {
    case "geoserver":
      return new VectorTile({
        ...parámetrosComunesRíosMedianos,
        source: crearFuenteVectorTiles(
          "https://geo.almanaqueazul.org/geoserver/gwc/service/tms/1.0.0/conectividad:rios_medianos@WebMercatorQuad@pbf/{z}/{x}/{-y}.pbf"
        ),
      });
    case "arcgis":
      return new VectorLayer({
        ...parámetrosComunesRíosMedianos,
        source: crearFuenteVectores("rios_medianos"),
      });
    case "pmtiles":
      return new VectorTile({
        ...parámetrosComunesRíosMedianos,
        source: new PMTilesVectorSource({
          url: "./data/rios_medianos.pmtiles",
        }),
      });
  }
})();

const capaRíosChicos = (() => {
  var parámetrosComunesRíosChicos = {
    ...parámetrosComunesRíos,
    name: "Ríos chicos",
    minZoom: 11,
  };
  switch (fuentes.capaRíosChicos) {
    case "geoserver":
      return new VectorTile({
        ...parámetrosComunesRíosChicos,
        source: crearFuenteVectorTiles(
          "https://geo.almanaqueazul.org/geoserver/gwc/service/tms/1.0.0/conectividad:rios_chicos@WebMercatorQuad@pbf/{z}/{x}/{-y}.pbf"
        ),
      });
    case "arcgis":
      return new VectorLayer({
        ...parámetrosComunesRíosChicos,
        source: crearFuenteVectores("rios_chicos"),
      });
    case "pmtiles":
      return new VectorTile({
        ...parámetrosComunesRíosChicos,
        source: new PMTilesVectorSource({
          url: "./data/rios_chicos.pmtiles",
        }),
      });
  }
})();

// const riosMedianosFuente = new VectorTileSource({
//   projection: "EPSG:3857",
//   tilePixelRatio: 1, // oversampling when > 1
//   tileGrid: tileGrid.createXYZ({ maxZoom: 16 }),
//   format: new MVT(),
//   url: "https://geo.almanaqueazul.org/geoserver/gwc/service/tms/1.0.0/conectividad:rios_medianos@WebMercatorQuad@pbf/{z}/{x}/{-y}.pbf",
// });

// const riosChicosFuente = new VectorTileSource({
//   projection: "EPSG:3857",
//   tilePixelRatio: 1, // oversampling when > 1
//   tileGrid: tileGrid.createXYZ({ maxZoom: 16 }),
//   format: new MVT(),
//   url: "https://geo.almanaqueazul.org/geoserver/gwc/service/tms/1.0.0/conectividad:rios_chicos@WebMercatorQuad@pbf/{z}/{x}/{-y}.pbf",
// });

// const capaRíosMedianos = new VectorTile({
//   name: "Ríos medianos",
//   tipo: "ríos",
//   source: riosMedianosFuente,
//   displayInLayerSwitcher: false,
//   minZoom: 11,
//   style: function (feature, resolution) {
//     const scheidegger = parseFloat(feature.get("scheidegger"));

//     var ancho =
//       estiloRiosScheidegger.find(graduatedCallback, scheidegger)[2] /
//       resolution;

//     return new Style({
//       stroke: new Stroke({
//         // color: "#adcff3",
//         color: "#fff",

//         width: ancho,
//       }),
//     });
//   },
// });

// const capaRíosChicos = new VectorTile({
//   name: "Ríos medianos",
//   tipo: "ríos",
//   source: riosChicosFuente,
//   displayInLayerSwitcher: false,
//   minZoom: 13,
//   style: function (feature, resolution) {
//     const scheidegger = parseFloat(feature.get("scheidegger"));

//     var ancho =
//       estiloRiosScheidegger.find(graduatedCallback, scheidegger)[2] /
//       resolution;

//     return new Style({
//       stroke: new Stroke({
//         // color: "#adcff3",
//         color: "#fff",

//         width: ancho,
//       }),
//     });
//   },
// });

// COSTA

// const capaCosta = new TileLayer({

const capaCosta = new VectorTile({
  className: "capaCosta",
  visible: true,
  displayInLayerSwitcher: false,
  source: new PMTilesVectorSource({
    url: "./data/costa_IGNTG_AA.pmtiles",
  }),
  style: function (feature, resolution) {
    var anchoCosta = 60 / resolution;
    const estiloCosta = new Style({
      stroke: new Stroke({
        color: "#53a9ff",
        width: anchoCosta,
      }),
    });
    return estiloCosta;
  },

  // source: new WMTS({
  //   url: "https://geo.almanaqueazul.org/geoserver/gwc/service/wmts",
  //   layer: "conectividad:IGNTG_costa",
  //   matrixSet: "WebMercatorQuad",
  //   format: "image/png",
  //   projection: projection,
  //   tileGrid: new WMTSTileGrid({
  //     tileSize: [256, 256],
  //     extent: [
  //       -2.003750834e7, -2.0037508345578436e7, 2.0037508345578436e7,
  //       2.003750834e7,
  //     ],
  //     origin: [-2.003750834e7, 2.003750834e7],
  //     resolutions: resolutions,
  //     matrixIds: gridNames,
  //   }),
  // }),
});

// FONDO

const fuenteCapaFondoAA = new GeoTIFF({
  sources: [
    {
      url: "./data/fondo_aa_con_mar_cog.tif",
    },
  ],
});

const capaFondoAA = new WebGLTile({
  source: new PMTilesRasterSource({
    url: "./data/fondo_AA.pmtiles",
    tileSize: [512, 512],
  }),
});

// const capaFondoAA = new WebGLTile({
//   visible: false,
//   displayInLayerSwitcher: false,
//   source: fuenteCapaFondoAA,
//   view: fuenteCapaFondoAA.getView(),
//   // source: new WMTS({
//   //   url: "https://geo.almanaqueazul.org/geoserver/gwc/service/wmts",
//   //   layer: "conectividad:fondo_aa_con_mar_cog",
//   //   matrixSet: "WebMercatorQuad",
//   //   format: "image/jpeg",
//   //   projection: projection,
//   //   tileGrid: new WMTSTileGrid({
//   //     tileSize: [256, 256],
//   //     extent: [
//   //       -2.003750834e7, -2.0037508345578436e7, 2.0037508345578436e7,
//   //       2.003750834e7, -9271287.267692242, 774022.1676409675,
//   //       -8530307.432495803, 1111931.1604355925,
//   //     ],
//   //     origin: [-2.003750834e7, 2.003750834e7],
//   //     //[-9271287.267692242, 7740221676409675],
//   //     //
//   //     resolutions: resolutions,
//   //     matrixIds: gridNames,
//   //   }),
//   // }),
// });

// GRUPO DE CAPAS VECTORIALES AA

const capasAA = new LayerGroup({
  title: "Mapa con bosques y ríos",
  visible: false,

  layers: [
    // new TileLayer({
    //   source: fuenteBaseAA,
    // }),
    capaFondoAA,
    //callesLayer,
    panamericanaLayer,
    capaPueblosGrandes,
    capaPueblosMedianos,
    capaPueblosChicos,
    capaRíosGrandes,
    capaRíosMedianos,
    capaRíosChicos,
    capaFeaturesLineal,
    capaCosta,

    // capaRíosChicos,
  ],
  baseLayer: true,
  fondo: "#cbdff8",
  colorStroke: "#fff",
  blendMode: "multiply",
});

// *** CROQUIS ***

// ADMIN

const fuenteProvincias = new PMTilesVectorSource({
  url: "./data/provincias_IGNTG_AA.pmtiles",
});

//new VectorTileSource({
// projection: "EPSG:3857",
// tilePixelRatio: 1,
// tileGrid: tileGrid.createXYZ({ maxZoom: 16 }),
// format: new MVT(),
// url: "https://geo.almanaqueazul.org/geoserver/gwc/service/tms/1.0.0/conectividad:provincias@WebMercatorQuad@pbf/{z}/{x}/{-y}.pbf",

//});

const fuenteDistritos = new PMTilesVectorSource({
  url: "./data/distritos_IGNTG_AA.pmtiles",
});

// const fuenteDistritos = new VectorTileSource({
//   projection: "EPSG:3857",
//   tilePixelRatio: 1,
//   tileGrid: tileGrid.createXYZ({ maxZoom: 16 }),
//   format: new MVT(),
//   url: "https://geo.almanaqueazul.org/geoserver/conectividad/gwc/service/tms/1.0.0/conectividad:distritos@WebMercatorQuad@pbf/{z}/{x}/{-y}.pbf",
// });

const fuenteCorregimientos = new PMTilesVectorSource({
  url: "./data/corregimientos_IGNTG_AA.pmtiles",
});

// const fuenteCorregimientos = new VectorTileSource({
//   projection: "EPSG:3857",
//   tilePixelRatio: 1,
//   tileGrid: tileGrid.createXYZ({ maxZoom: 16 }),
//   format: new MVT(),
//   url: "https://geo.almanaqueazul.org/geoserver/conectividad/gwc/service/tms/1.0.0/conectividad:corregimientos@WebMercatorQuad@pbf/{z}/{x}/{-y}.pbf",
// });

const provinciasEtiquetasFuente = new VectorSource({
  projection: "EPSG:3857",
  format: new GeoJSON(),
  //url: "https://geo.almanaqueazul.org/geoserver/conectividad/ows?service=WFS&version=1.0.0&request=GetFeature&typeName=conectividad%3Aprovincias_etiquetas&maxFeatures=50&outputFormat=application%2Fjson",
  url: "./data/provincias_etiquetas_3857.geojson",
});

const estiloProvincias = new Style({
  stroke: new Stroke({
    color: "#1a854a",
    width: 2,
  }),
  fill: new Fill({
    color: "#d6ebd8",
  }),
});

const estiloDistritos = new Style({
  stroke: new Stroke({
    color: "#1a854a",
    width: 1,
  }),
  fill: new Fill({
    color: "#FFFFFF00",
  }),
});

const estiloCorregimientos = new Style({
  stroke: new Stroke({
    color: "#1a854a",
    width: 0.5,
    lineDash: [5, 5],
  }),
  fill: new Fill({
    color: "#FFFFFF00",
  }),
});

const estiloProvinciasEtiquetas = new Style({
  text: new Text({
    fill: new Fill({
      color: "#1a854a",
    }),
    overflow: true,
    placement: "line",
    baseline: "middle",
    maxAngle: 180,
    rotateWithView: true,
  }),
});

const capaProvincias = new VectorTile({
  name: "Provincias",
  tipo: "admin",
  displayInLayerSwitcher: false,
  source: fuenteProvincias,
  extent: [-9268746, 546138, -8471894, 1141657],
  style: estiloProvincias,
  renderMode: "vector",
  declutter: true,
});

const capaDistritos = new VectorTile({
  name: "Distritos",
  tipo: "admin",
  minZoom: 9.9,
  displayInLayerSwitcher: false,
  source: fuenteDistritos,
  extent: [-9245339, 804006, -8589281, 1079050],
  // style: estiloDistritos,
  declutter: false,
  style: function (feature, resolution) {
    const props = feature.getProperties();

    if (props.layer === "distritos_label_points") {
      estiloDistritos.text_ = new Text({
        fill: new Fill({
          color: "#1a854a",
        }),
        overflow: true,
        placement: "point",
        baseline: "middle",
        rotateWithView: false,
      });

      const label = props.distrito;
      let tamaño = 15;
      const espaciado = 1;

      //tamaño = (tamaño * 250) / resolution;

      const fuente = `${tamaño}px Delicious-Bold`;

      const labelConEspacios = ponerEspacios(label, espaciado);

      estiloDistritos.getText().setText(labelConEspacios);
      estiloDistritos.getText().setFont(fuente);
    } else {
      estiloDistritos.text_ = null;
    }

    return estiloDistritos;
  },
});

const capaCorregimientos = new VectorTile({
  name: "Corregimientos",
  tipo: "admin",
  minZoom: 11.9,
  displayInLayerSwitcher: false,
  source: fuenteCorregimientos,
  extent: [-9245339, 804006, -8589281, 1079050],
  declutter: false,
  style: function (feature, resolution) {
    const props = feature.getProperties();

    if (props.layer === "corregimientos_label_points") {
      estiloCorregimientos.text_ = new Text({
        fill: new Fill({
          color: "#1a854a",
        }),
        overflow: true,
        placement: "point",
        baseline: "middle",
        rotateWithView: false,
      });

      const label = props.corregimiento;
      let tamaño = 13;

      //tamaño = (tamaño * 250) / resolution;

      const fuente = `${tamaño}px Delicious-roman`;

      estiloCorregimientos.getText().setText(label);
      estiloCorregimientos.getText().setFont(fuente);
    } else {
      estiloCorregimientos.text_ = null;
    }

    // console.log(
    //   "Capa: ",
    //   props.layer,
    //   "corregimiento: ",
    //   props.distrito,
    //   " estilo: ",
    //   estiloDistritos
    // );

    return estiloCorregimientos;
  },
});

const capaProvinciasEtiquetas = new VectorLayer({
  name: "Etiquetas provincias",
  tipo: "admin",
  displayInLayerSwitcher: false,
  source: provinciasEtiquetasFuente,
  extent: [-9268746, 546138, -8471894, 1141657],
  style: function (feature, resolution) {
    const label = feature.get("etiqueta");
    let tamaño = feature.get("fuente") || 15;
    const espaciado = feature.get("espaciado") || 1;

    tamaño = (tamaño * 250) / resolution;

    const fuente = `${tamaño}px Delicious-Bold`;

    const labelConEspacios = ponerEspacios(label, espaciado);

    estiloProvinciasEtiquetas.getText().setText(labelConEspacios.toUpperCase());
    estiloProvinciasEtiquetas.getText().setFont(fuente);

    return estiloProvinciasEtiquetas;
  },
  declutter: false,
});

// PUEBLOS GRANDES

const capaPueblosGrandesCroquis = new VectorLayer({
  name: "Pueblos Grandes",
  displayInLayerSwitcher: false,
  source: fuentePueblosGrandes,
  zIndex: 1000,
  style: function (feature) {
    const label = feature.get("NOMB_MOST").replace("\\", "\n");
    estiloPueblosGrandesCroquis.getText().setText(label);
    return [estiloPueblosGrandesCroquis];
  },
  declutter: true,
});

const capaPueblosMedianosCroquis = new VectorLayer({
  name: "Pueblos Medianos",
  displayInLayerSwitcher: false,
  source: new VectorSource({
    format: new GeoJSON(),
    url: "./data/pueblos_2.geojson",
  }),
  zIndex: 1000,
  minZoom: 10,
  style: function (feature) {
    const label = feature.get("NOMB_MOST").replace("\\", "\n");
    estiloPueblosChicosMedianosCroquis.getText().setText(label);

    return [estiloPueblosChicosMedianosCroquis];
  },
  declutter: true,
});

const capaPueblosChicosCroquis = new VectorLayer({
  name: "Pueblos chicos",
  displayInLayerSwitcher: false,
  source: new VectorSource({
    format: new GeoJSON(),
    url: "./data/pueblos_3.geojson",
  }),
  zIndex: 1000,

  minZoom: 12,
  style: function (feature) {
    const label = feature.get("NOMB_MOST").replace("\\", "\n");
    estiloPueblosChicosMedianosCroquis.getText().setText(label);

    return [estiloPueblosChicosMedianosCroquis];
  },
  declutter: true,
});

// GRATICULA

const graticulaCroquis = new Graticule({
  displayInLayerSwitcher: false,

  strokeStyle: new Stroke({
    color: "#1a854a",
    width: 1,
    lineDash: [0.5, 4],
  }),
  lonLabelStyle: new Text({
    font: "12px Delicious-roman",
    textBaseline: "bottom",
    fill: new Fill({
      color: "rgba(0,0,0,1)",
    }),
    stroke: new Stroke({
      color: "rgba(255,255,255,1)",
      width: 3,
    }),
  }),
  latLabelStyle: new Text({
    font: "12px Delicious-roman",
    textBaseline: "bottom",
    fill: new Fill({
      color: "rgba(0,0,0,1)",
    }),
    stroke: new Stroke({
      color: "rgba(255,255,255,1)",
      width: 3,
    }),
  }),
  showLabels: true,
  //wrapX: false,
  extent: [-9298746, 546138, -8471894, 1141657],
});

// LEYENDA

const capaLeyendaCroquis = new VectorLayer({
  name: "Leyenda croquis",
  tipo: "croquis",
  displayInLayerSwitcher: false,
  source: new VectorSource({
    format: new GeoJSON(),
    url: "./data/punto_leyenda_croquis.geojson",
  }),
  style: function (feature, resolution) {
    var anchoLeyenda = 450 / resolution;
    const leyenda = new Style({
      image: new Icon({
        src: "./data/iconos/leyenda_croquis_curvas.svg",
        scale: anchoLeyenda,
      }),
    });
    return leyenda;
  },
});

// GRUPO DE CAPAS CROQUIS

const capasCroquis = new LayerGroup({
  title: "Croquis",
  visible: true,

  layers: [
    capaPueblosGrandesCroquis,
    capaPueblosMedianosCroquis,
    capaPueblosChicosCroquis,
    capaProvincias,
    capaDistritos,
    capaCorregimientos,
    capaProvinciasEtiquetas,
    graticulaCroquis,
    capaLeyendaCroquis,
    //    capaCosta,
  ],
  baseLayer: true,
  fondo: "#fff",
  colorStroke: "#1a854a",
  blendMode: "multiply",
});

// FONDO SATÉLITE ARCGIS

const capaWorldImageryArcGIS = new TileLayer({
  title: "ArcGIS satelital",
  visible: false,
  source: new XYZ({
    attributions:
      'Tiles © <a href="https://services.arcgisonline.com/ArcGIS/' +
      'rest/services/World_Topo_Map/MapServer">ArcGIS</a>',
    url:
      "https://server.arcgisonline.com/ArcGIS/rest/services/" +
      "World_Imagery/MapServer/tile/{z}/{y}/{x}",
  }),
  baseLayer: true,
  blendMode: "overlay",
});

// *** GRUPO DE CAPAS DE FONDO ***
// MAPA AA, CROQUIS, OPENSTREETMAP, BING

const capasFondo = new LayerGroup({
  title: "Fondo",
  openInLayerSwitcher: true,
  layers: [
    // new WebGLTile({
    //   title: "Bing sat",
    //   visible: false,
    //   preload: Infinity,
    //   baseLayer: true,
    //   source: new BingMaps({
    //     key: "Ap1g5eylYyrAgHKTl9dB8GBZpT86u0QIB4rmwCPyei3WoOk40FLcgVhVPkuEK2dl",
    //     imagerySet: "Aerial",
    //   }),
    // }),
    capaWorldImageryArcGIS,

    new WebGLTile({
      title: "OpenStreetMap",
      visible: false,
      baseLayer: true,
      source: new OSM(),
    }),
    capasAA,
    capasCroquis,

    //capaFondoAA,
  ],
});

// Define a KMZ format class by subclassing ol/format/KML

// class KMZ extends KML {
//   constructor(opt_options) {
//     const options = opt_options || {};
//     options.iconUrlFunction = getKMLImage;
//     super(options);
//   }

//   getType() {
//     return "arraybuffer";
//   }

//   readFeature(source, options) {
//     const kmlData = getKMLData(source);
//     return super.readFeature(kmlData, options);
//   }

//   readFeatures(source, options) {
//     const kmlData = getKMLData(source);
//     return super.readFeatures(kmlData, options);
//   }
// }

const dragAndDropInteraction = new DragAndDrop({
  //formatConstructors: [KMZ, GPX, GeoJSON, IGC, KML, TopoJSON],
  formatConstructors: [GPX, GeoJSON, IGC, TopoJSON],
});

// ******************
// ****** MAPA ******
// ******************

const mapa = new Map({
  interactions: defaultInteractions().extend([
    new DragRotateAndZoom(),
    dragAndDropInteraction,
  ]),
  target: "contenedorMapa",
  controls: defaultControls({
    zoom: false,
  }).extend([
    new ScaleLine({
      units: "metric",
    }),
  ]),
  layers: [
    capasFondo,
    // capaConectividad35k,
    // capaConectividad6k,
    capaInfra,
    capaMineria,
    capaReferencia,
    capaCorredoresNaturales,
    capaCobertura,
    capaTMF,
    capaHansen,
    capaSINAP,

    // capaInterpretación,
    capaMedir,
  ],
  view: new View({
    projection: projection,
    center: getCenter(sourceExtent),
    extent: sourceExtent,
    zoom: 6,
    maxZoom: 16,
    constrainResolution: true,
    smoothResolutionConstraint: true,
  }),
});

// capaConectividad6k.setOpacity(0.6);
// capaConectividad35k.setOpacity(0.6);
// capaInterpretación.setOpacity(0.5);

if (capaExtra) {
  mapa.addLayer(capaExtra);
}

// Observar cuando se agrega capa de conectividad y poner blend mode

const observer = new MutationObserver(function (mutationsList) {
  mutationsList.forEach(function (mutation) {
    Array.from(mutation.addedNodes).forEach(function (addedNode) {
      if (addedNode.classList.contains("capaConectividad")) {
        const baseLayerValues = obtenerValoresCapaBase();
        ponerModoBlend(baseLayerValues.blendMode);
        //observer.disconnect();
      }
    });
  });
});

observer.observe(document.querySelector(".ol-layers"), {
  subtree: true,
  childList: true,
});

// Descomentar lo de abajo para hacer debugging de zoom
// mapa.getView().on("change:resolution", (event) => {
//   //const nivelZoom = document.getElementById("nivelZoom");
//   var currZoom = mapa.getView().getZoom();
//   console.log("Zoom:", currZoom);
// });

// Habiendo definido todas las capas, calcular el máximo rango de años de las capas de deforestación
// TODO: traerse la función para acá

function calcularMaxRangoAños() {
  // Gets max possible tree cover years range so we can generate all possible needed random colours
  // runs every time we generate a random colour pallete
  var max,
    min = false;
  mapa.getLayers().forEach((capaMapa) => {
    if (capaMapa.values_.tipo == "pérdida") {
      if (!max) {
        max = capaMapa.getSource().sourceInfo_[0].max;
        min = capaMapa.getSource().sourceInfo_[0].min;
      } else {
        if (max < capaMapa.getSource().sourceInfo_[0].max) {
          max = capaMapa.getSource().sourceInfo_[0].max;
        }
        if (min > capaMapa.getSource().sourceInfo_[0].min) {
          min = capaMapa.getSource().sourceInfo_[0].min;
        }
      }
      // rango = capaMapa.getSource().sourceInfo_[0].max - capaMapa.getSource().sourceInfo_[0].min;
      // if (rango > maxRango) {
      //   maxRango = rango
      // }
    }
  });

  return [min, max];
}

maxRangoAños = calcularMaxRangoAños();

// ******************
// *** CONTROLES ***
// ******************

// LAYER SWITCHER

const layerSwitcher = new LayerSwitcher({
  //target: contenedorCapas,
  mouseover: false,
  reordering: false,
  // collapsed: true,
  onchangeCheck: function (capaCambio) {
    // Toggle layers that are mutually exclusive. Adapted from function used in layerSwitcher for base layers
    var tipo = capaCambio.values_.tipo;

    if (tipo == "pérdida") {
      alternarCapas(capaCambio, tipo);
      layerSwitcher.drawPanel();

      agregarBotonesOpacidades();

      ajustarEstiloPérdida();
    }

    if (capaCambio.values_.fondo) {
      // Si la capa que se ha puesto es una capa de fondo
      ponerModoBlend(capaCambio.values_.blendMode);
    } else {
      document.body.style.background = "#fff";
    }
  },
});

mapa.addControl(layerSwitcher);

// Crear un array con los contenidos (li-content) del layer switcher

agregarBotonesOpacidades();

function agregarBotonesOpacidades() {
  setTimeout(() => {
    var opacidades = document.getElementsByClassName("li-content");
    var arrayOpacidades = [];

    arrayOpacidades = Array.from(opacidades);

    arrayOpacidades.forEach(function (controlOpacidadDeCapa) {
      controlOpacidadDeCapa.querySelector(
        ".layerswitcher-opacity"
      ).style.display = "none";

      var div = window.document.createElement("div");
      div.className = "botonOpacidad";
      div.setAttribute("title", "Opacidad");

      div.addEventListener(
        "click",
        function (e) {
          console.log("evento:", e);
          if (e.target !== this) return;
          e.stopPropagation();
          e.preventDefault();

          var sliderOpacity = this.parentNode.querySelector(
            ".layerswitcher-opacity"
          );

          var opacityLabel = this.parentNode.querySelector(
            ".layerswitcher-opacity-label"
          );

          if (sliderOpacity.style.display == "none") {
            sliderOpacity.style.display = opacityLabel.style.display = "block";
          } else {
            sliderOpacity.style.display = opacityLabel.style.display = "none";
          }
        },
        false
      );

      controlOpacidadDeCapa.prepend(div);
    });
  }, 1);
}
// Toggle forest loss layers

// capaHansen.on("change:visible", function (e) {
//   if (e.target.get(e.key)) {
//     console.log("prendida");
//     capaSINAP.setVisible = false;
//   } else {
//     capaSINAP.setVisible = true;
//   }

//   //console.log("Countries", e.target.get(e.key));
// });

let selected = null;
//let capa = null;

mapa.on("pointermove", (event) => {
  identificarCosas(event);
});

mapa.on("singleclick", (event) => {
  identificarCosas(event);
});

// AÑOS DE PÉRDIDA ARBÓREA (SLIDER)

var toggleEvent = new Event("toggle", {
  bubbles: true,
  cancelable: true,
});

toggleEvent.collapsed = !pérdidaArbórea.classList.contains("ol-forceopen");
self.dispatchEvent(toggleEvent);

botónPérdidaArbórea.onclick = function () {
  pérdidaArbórea.classList.toggle("ol-forceopen");
  pérdidaArbórea.classList.add("ol-collapsed");
  self.dispatchEvent(toggleEvent);
};

const sliderFormat = {
  to: function (value) {
    if (value == sliderMin) {
      return "min";
    } else if (value == sliderMax) {
      return "max";
    } else {
      return "";
    }
  },
  // from: function (value) {
  //   return 30;
  // },
};

function filterPips(value) {
  if (value % 5 == 0) {
    return 1;
  } else if (value % 1 == 0) {
    return 0;
  } else {
    return -1;
  }
}

var sliderMin = 2001;
var sliderMax = 2023;

noUiSlider.create(sliderAñoPérdida, {
  start: [sliderMin, sliderMax],
  connect: true,
  orientation: "horizontal",
  direction: "ltr",
  tooltips: [
    wNumb({
      decimals: 0,
      encoder: function (value) {
        return value;
      },
    }),
    wNumb({
      decimals: 0,
      encoder: function (value) {
        return value;
      },
    }),
  ],
  step: 1,
  range: {
    min: sliderMin,
    max: sliderMax,
  },
  pips: {
    mode: "steps",
    filter: filterPips,
    //format: sliderFormat,
    density: 1,
  },
});

var fondoSlider = document.querySelector(".noUi-connect");
alternarCapas();
//console.log("Capa actual: ", actualCapaPérdida);
ajustarEstiloPérdida();

// let colorCapa = {};
// // colorCapa["slider6k"] = color2_6k.toString();
// colorCapa["sliderAñoPérdida"] = color2_6k.toString();
// // colorCapa["slider35k"] = color2_35k.toString();

// // colorFondoControlVolumen(slider6k, "rgb(" + colorCapa["slider6k"] + ")");
// colorFondoControlVolumen(
//   sliderAñoPérdida,
//   "rgb(" + colorCapa["slider6k"] + ")"
// );

// colorFondoControlVolumen(slider35k, "rgb(" + colorCapa["slider35k"] + ")");

// slider6k.noUiSlider.on("slide", function () {
//   var rango = slider6k.noUiSlider.get();

//   var conectMin6k = parseInt(rango[0]);
//   var conectMax6k = parseInt(rango[1]);
//   var nuevoestilo = hacerPaleta(conectMin6k, conectMax6k, color1_6k, color2_6k);

//   capaConectividad6k.setStyle(nuevoestilo);
//   capaConectividad6k.changed();
// });

// Run on slider change ("end") to set style and save state

sliderAñoPérdida.noUiSlider.on("end", function () {
  console.log("end");
  console.log("matrtiz: ", getComputedStyle(fondoSlider).transform);

  if (actualizarEstadoPérdida("rango")) {
    ajustarEstiloPérdida();
  }
});

sliderAñoPérdida.noUiSlider.on("set", function () {
  console.log("set");
  console.log("matrtiz: ", getComputedStyle(fondoSlider).transform);

  if (actualizarEstadoPérdida("rango")) {
    ajustarEstiloPérdida();
  }
});

botonesColor.forEach((input) => {
  input.addEventListener("change", cambiarColor);
});

//arrastrarElemento(pérdidaArbórea);
//alternarCapas();

// *** MODAL ***

infoMapa.onclick = function () {
  acercaDe.style.display = "block";
};

cerrarModal.onclick = function () {
  acercaDe.style.display = "none";
};

window.onclick = function (event) {
  if (event.target == acercaDe) {
    acercaDe.style.display = "none";
  }
};

// *** BOTÓN PANTALLA COMPLETA ***

if (document.fullscreenEnabled || document.webkitFullscreenEnabled) {
  document.addEventListener("DOMContentLoaded", (event) => {
    toggleBtn.hidden = false;
  });

  toggleBtn.addEventListener("click", function () {
    if (document.fullscreen) {
      document.exitFullscreen();
    } else if (document.webkitFullscreenElement) {
      document.webkitCancelFullScreen();
    } else if (document.documentElement.requestFullscreen) {
      document.documentElement.requestFullscreen();
    } else {
      document.documentElement.webkitRequestFullScreen();
    }
  });

  document.addEventListener("fullscreenchange", handleFullscreen);
  document.addEventListener("webkitfullscreenchange", handleFullscreen);
}

// *** HERRAMIENTA DE MEDIR ***

let sketch;
let midiendoDist = false;
let midiendoArea = false;

/**
 * The help tooltip element.
 * @type {HTMLElement}
 */
let helpTooltipElement;

/**
 * Overlay to show the help messages.
 * @type {Overlay}
 */
let helpTooltip;

/**
 * The measure tooltip element.
 * @type {HTMLElement}
 */
let measureTooltipElement;

/**
 * Overlay to show the measurement.
 * @type {Overlay}
 */
let measureTooltip = [];

let helpMsg = "Clic para empezar a medir<br/>Doble clic para terminar";

/**
 * Message to show when the user is drawing a polygon.
 * @type {string}
 */
const continuePolygonMsg = "";

/**
 * Message to show when the user is drawing a line.
 * @type {string}
 */
const continueLineMsg = "";

let dibujoMedir; // global so we can remove it later

/**
 * Format length output.
 * @param {LineString} line The line.
 * @return {string} The formatted length.
 */
const formatLength = function (line) {
  const length = getLength(line);
  let output;
  if (length > 100) {
    output = Math.round((length / 1000) * 100) / 100 + " " + "km";
  } else {
    output = Math.round(length * 100) / 100 + " " + "m";
  }
  return output;
};

/**
 * Format area output.
 * @param {Polygon} polygon The polygon.
 * @return {string} Formatted area.
 */
const formatArea = function (polygon) {
  const area = getArea(polygon);
  let output;
  if (area > 10000) {
    output =
      Math.round((area / 10000) * 100) / 100 +
      " " +
      "ha<br/>" +
      Math.round((area / 1000000) * 100) / 100 +
      " " +
      "km<sup>2</sup>";

    //output = Math.round((area / 1000000) * 100) / 100 + " " + "km<sup>2</sup>";
  } else {
    output = Math.round(area * 100) / 100 + " " + "m<sup>2</sup>";
  }
  return output;
};

botonMedirDist.addEventListener("click", function () {
  if (midiendoArea) {
    apagarMidiendoArea();
  }

  if (midiendoDist) {
    apagarMidiendoDist();
  } else {
    midiendoDist = true;
    addInteraction("LineString");
    svgMedirDist.setAttribute("stroke", "#ffcc33");
    contenedorMapa.style.cursor = "none";

    mapa.addInteraction(dibujoMedir);
    createMeasureTooltip();
    createHelpTooltip();
  }
});

botonMedirArea.addEventListener("click", function () {
  var bb = {
    xmin: -8938620,
    ymin: 971453,
    xmax: -8915019,
    ymax: 999407,
  };

  if (midiendoDist) {
    apagarMidiendoDist();
  }

  if (midiendoArea) {
    apagarMidiendoArea();
  } else {
    midiendoArea = true;
    addInteraction("Polygon");
    svgMedirArea.setAttribute("stroke", "#ffcc33");
    contenedorMapa.style.cursor = "none";

    mapa.addInteraction(dibujoMedir);
    createMeasureTooltip();
    createHelpTooltip();
  }
});

dragAndDropInteraction.on("addfeatures", function (event) {
  const vectorSource = new VectorSource({
    features: event.features,
  });

  mapa.addLayer(
    new VectorLayer({
      source: vectorSource,
      title: event.file.name,
    })
  );
  mapa.getView().fit(vectorSource.getExtent());

  showAlertModal("Capa " + event.file.name + " agregada");
  // zip = new JSZip();
});

// *****************
// *** FUNCIONES ***
// *****************

function obtenerValoresCapaBase() {
  let valores = {};
  mapa.getLayers().forEach((capaMapa) => {
    if (capaMapa instanceof LayerGroup) {
      capaMapa.getLayers().forEach((subcapa) => {
        if (
          subcapa instanceof LayerGroup &&
          subcapa.values_.baseLayer &&
          subcapa.getVisible()
        ) {
          valores = subcapa.values_;
        }
      });
    }
  });
  return valores;
}

function activarDesactivarControlVolumen(slider, state) {
  if (state) {
    slider.noUiSlider.enable();
    //var a = slider.attributes["id"].value;
    colorFondoControlVolumen(
      slider,
      //"rgb(" + colorCapa[slider.attributes["id"].value] + ")"
      "#fff"
    );
  } else {
    slider.noUiSlider.disable();
    colorFondoControlVolumen(slider, "lightgrey");
  }
}

function colorFondoControlVolumen(slider, color) {
  slider.children[0].children[1].children[0].style.background = color;
}

function getColorStops(name, min, max, steps, reverse) {
  const delta = (max - min) / (steps - 1);
  const stops = new Array(steps * 2);
  const colors = colormap({ colormap: name, nshades: steps, format: "rgba" });
  if (reverse) {
    colors.reverse();
  }
  for (let i = 0; i < steps; i++) {
    stops[i * 2] = min + i * delta;
    stops[i * 2 + 1] = colors[i];
  }
  return stops;
}

// MEDIDOR DE DISTANCIAS Y ÁRAS

function apagarMidiendoDist() {
  midiendoDist = false;
  svgMedirDist.setAttribute("stroke", "#fff");
  contenedorMapa.style.cursor = "crosshair";

  helpTooltipElement.parentNode.removeChild(helpTooltipElement);
  helpTooltipElement = null;
  fuenteMedir.clear();

  measureTooltip.forEach((tooltip) => {
    mapa.removeOverlay(tooltip);
  });

  measureTooltip = [];

  mapa.removeInteraction(dibujoMedir);
}

function apagarMidiendoArea() {
  midiendoArea = false;
  svgMedirArea.setAttribute("stroke", "#fff");
  contenedorMapa.style.cursor = "crosshair";

  helpTooltipElement.parentNode.removeChild(helpTooltipElement);
  helpTooltipElement = null;
  fuenteMedir.clear();

  measureTooltip.forEach((tooltip) => {
    mapa.removeOverlay(tooltip);
  });

  measureTooltip = [];

  mapa.removeInteraction(dibujoMedir);
}

function addInteraction(tipo) {
  // const type = typeSelect.value == 'area' ? 'Polygon' : 'LineString';
  //const type = "LineString";
  dibujoMedir = new Draw({
    source: fuenteMedir,
    type: tipo,
    style: [
      new Style({
        fill: new Fill({
          color: "rgba(255, 255, 255, 0.2)",
        }),
        stroke: new Stroke({
          color: "rgba(0, 0, 0, 0.5)",
          lineDash: [10, 10],
          width: 2,
        }),
        image: new CircleStyle({
          radius: 5,
          stroke: new Stroke({
            color: "rgba(0, 0, 0, 0.7)",
          }),
          fill: new Fill({
            color: "rgba(255, 255, 255, 0.2)",
          }),
        }),
      }),

      new Style({
        image: new CircleStyle({
          radius: 0.5,
          stroke: new Stroke({
            color: "rgba(0, 0, 0, 0.7)",
          }),
          fill: new Fill({
            color: "rgba(255, 255, 255, 0.2)",
          }),
        }),
      }),
    ],
  });

  let listener;

  dibujoMedir.on("drawstart", function (evt) {
    // set sketch
    sketch = evt.feature;

    /** @type {import("../src/ol/coordinate.js").Coordinate|undefined} */
    let tooltipCoord = evt.coordinate;

    listener = sketch.getGeometry().on("change", function (evt) {
      const geom = evt.target;
      let output;
      if (geom instanceof Polygon) {
        output = formatArea(geom);
        tooltipCoord = geom.getInteriorPoint().getCoordinates();
      } else if (geom instanceof LineString) {
        output = formatLength(geom);
        tooltipCoord = geom.getLastCoordinate();
      }
      measureTooltipElement.innerHTML = output;
      measureTooltip.at(-1).setPosition(tooltipCoord);
    });
  });

  dibujoMedir.on("drawend", function (evt) {
    var areaDeforestada = 0;
    const geom = evt.feature.values_.geometry;
    if (geom instanceof Polygon) {
      var geomGeoJSON = crearPoligonoDeFlat(geom.flatCoordinates);
      //calculoDeforestación(geomGeoJSON, "area");
      calculoDeforestación(geomGeoJSON);
    } else {
      finalizarTooltip();
    }

    // const coordenadas = geom.flatCoordinates;
  });
  unByKey(listener);
}

function crearPoligonoDeFlat(coordenadasFlat) {
  // const coordenadasFlat = geom.flatCoordinates;
  // Convertir a array bidimensional con puntos del poligono agrupados para luego poner en geojson
  var coordenadasArrays = [];
  for (var i = 0; i < coordenadasFlat.length; i = i + 2)
    coordenadasArrays.push(coordenadasFlat.slice(i, i + 2));

  var geomGeoJSON = {
    type: "FeatureCollection",
    crs: {
      type: "name",
      properties: { name: "urn:ogc:def:crs:EPSG::3857" },
    },

    features: [
      {
        type: "Feature",
        properties: {},
        geometry: {
          type: "Polygon",
          coordinates: [coordenadasArrays],
        },
      },
    ],
  };

  return geomGeoJSON;
}

async function calculoDeforestación(poligono, enviador, ubicación) {
  // enviador y ubicación son por el intento de medir deforestación en polígonos de capas (distritos)
  //
  const nombreCapa = actualCapaPérdida.getProperties().name;
  const fuente = actualCapaPérdida.getSource().sourceInfo_[0];
  const nombreCorto = fuente.apodo;
  const ajuste = parseInt(fuente.ajuste) || 0;
  const estadoMin = parseInt(estadoCapasPérdida[nombreCapa].min);
  const estadoMax = parseInt(estadoCapasPérdida[nombreCapa].max);
  const archivoTIFF = new URL(fuente.url, document.baseURI).href;

  var georaster = await geoblazeParse(archivoTIFF);

  var histograma = [];
  histograma = await geoblazeHistogram(georaster, poligono, {
    scaleType: "nominal",
  });

  var areapixel = georaster.pixelHeight * georaster.pixelWidth;

  const areaDeforestada = calcularAreaDeforestada(
    histograma[0],
    estadoMin + ajuste,
    estadoMax + ajuste,
    areapixel
  );

  var infoDeforestación = {
    nombreCorto: nombreCorto,
    min: estadoMin,
    max: estadoMax,
    deforestación: areaDeforestada,
  };

  await alimentarTooltip(infoDeforestación, enviador, ubicación);

  return infoDeforestación;
}

function calcularAreaDeforestada(histograma, min, max, areapixel) {
  var metrosDeforestados = 0;
  for (const año in histograma) {
    if (año >= min && año <= max) {
      metrosDeforestados += histograma[año] * areapixel;
    }
  }
  return metrosDeforestados / 10000;
}

function alimentarTooltip(infoDeforestación, enviador, ubicación) {
  var rango = "";
  if (infoDeforestación.min == infoDeforestación.max) {
    rango = infoDeforestación.min;
  } else {
    rango = infoDeforestación.min + "-" + infoDeforestación.max;
  }

  // // Esto pertenece al intento de medir def en distritos
  // if (enviador != "area") {
  //   console.log("measuretooltip: ", measureTooltip);
  //   measureTooltip.at(-1).setPosition(ubicación);
  //   measureTooltipElement.innerHTML += "Distrito de " + enviador;
  // }

  measureTooltipElement.innerHTML +=
    "<br/>Pérdida (" +
    infoDeforestación.nombreCorto +
    ", " +
    rango +
    "): " +
    Math.round(infoDeforestación.deforestación) +
    " ha";

  finalizarTooltip();
}

function finalizarTooltip() {
  measureTooltipElement.className = "ol-tooltip ol-tooltip-static";
  measureTooltip.at(-1).setOffset([0, -7]);
  // unset sketch
  sketch = null;
  // unset tooltip so that a new one can be created
  measureTooltipElement = null;
  createMeasureTooltip();
}

/**
 * Creates a new help tooltip
 */
function createHelpTooltip() {
  if (helpTooltipElement) {
    helpTooltipElement.parentNode.removeChild(helpTooltipElement);
    helpTooltipElement = null;
  }
  helpTooltipElement = document.createElement("div");
  helpTooltipElement.className = "ol-tooltip hidden mirsito";
  helpTooltip = new Overlay({
    element: helpTooltipElement,
    offset: [-15, 0],
    positioning: "center-right",
  });
  helpTooltipElement.innerHTML = helpMsg;

  mapa.addOverlay(helpTooltip);
}

/**
 * Creates a new measure tooltip
 */
function createMeasureTooltip() {
  if (measureTooltipElement) {
    measureTooltipElement.parentNode.removeChild(measureTooltipElement);
  }
  measureTooltipElement = document.createElement("div");
  measureTooltipElement.className = "ol-tooltip ol-tooltip-measure";
  measureTooltipElement.setAttribute("draggable", true);
  measureTooltip.push(
    new Overlay({
      element: measureTooltipElement,
      offset: [0, -15],
      positioning: "bottom-center",
      stopEvent: false,
      insertFirst: false,
      stopEvent: false,
    })
  );
  // agregar el último tooltip creado arriba con push
  mapa.addOverlay(measureTooltip.at(-1));

  var drag = new DragOverlay({
    overlays: measureTooltip.at(-1),
  });
  mapa.addInteraction(drag);
}

function cambiarColor(evento) {
  // Generate new random pallete

  coloresAleatorios = iwanthue(maxRangoAños[1] - maxRangoAños[0] + 1, {
    colorSpace: "all",
    clustering: "force-vector",
  });
  console.log("coloresAleatorios maxrango:", coloresAleatorios);

  ajustarEstiloPérdida();
}

// function arrastrarElemento(elmnt) {
//   // Esta es la función que permite arrastrar el botón y el slider de revelar conectividad
//   // También se encarga de determinar la posición del slider relativa al botón según la posición de este

//   let pos1 = 0,
//     pos2 = 0,
//     pos3 = 0,
//     pos4 = 0;
//   let limiteAncho = 450;
//   const header = document.getElementById(`${elmnt.id}header`);
//   const dragMouseDown = (e) => {
//     e.preventDefault();
//     pos3 = e.clientX;
//     pos4 = e.clientY;
//     document.onmouseup = cerrarArrastrarElemento;
//     document.onmousemove = arrastre;
//     vh = Math.max(
//       document.documentElement.clientHeight || 0,
//       window.innerHeight || 0
//     );
//     vw = Math.max(
//       document.documentElement.clientWidth || 0,
//       window.innerWidth || 0
//     );
//   };
//   const arrastre = (e) => {
//     e.preventDefault();
//     pos1 = pos3 - e.clientX;
//     pos2 = pos4 - e.clientY;
//     pos3 = e.clientX;
//     pos4 = e.clientY;

//     let bottom =
//       elmnt.offsetParent.offsetHeight -
//       elmnt.offsetTop -
//       elmnt.offsetHeight +
//       pos2;

//     let left = elmnt.offsetLeft - pos1;

//     elmnt.style.bottom = bottom + "px";
//     elmnt.style.left = left + "px";

//     // Está hacia arriba el botón? Poner el slider abajo. O lo contrario

//     if (bottom > vh - 150) {
//       contenedorPrincipalSlider.style.top = "30px";
//       contenedorPrincipalSlider.style.bottom = null;
//     } else {
//       contenedorPrincipalSlider.style.bottom = "30px";
//       contenedorPrincipalSlider.style.top = null;
//     }

//     // 768 es el límite para estilos de celulares que está en el CSS. Cambiar si eso cambia
//     // En celulares el ancho del slider es 40vh más 40px de padding. Si no es 450px más 40px de padding.
//     // Pero en pantallas está ok si se pierde un poquitín por arriba o por la derecha antes de cambiar de lado, así que no sumamos el padding al límite

//     if (vw <= 768) {
//       limiteAncho = vw * 0.4 + 40;
//     } else {
//       limiteAncho = 450;
//     }

//     if (left > vw - limiteAncho) {
//       contenedorPrincipalSlider.style.right = "0px";
//       contenedorPrincipalSlider.style.left = null;
//     } else {
//       contenedorPrincipalSlider.style.left = "0px";
//       contenedorPrincipalSlider.style.right = null;
//     }
//   };
//   const cerrarArrastrarElemento = () => {
//     document.onmouseup = null;
//     document.onmousemove = null;
//   };
//   if (header) {
//     header.addEventListener("mousedown", dragMouseDown);
//   } else {
//     elmnt.addEventListener("mousedown", dragMouseDown);
//   }
// }

// INFO BOXES
function parseWMSString(str) {
  const regex = /(\w+)\s*=\s*((?:\[\w\s\(\),]+\])|(?:[^:\n]*))/gm;
  const fields = {};

  for (const match of str.matchAll(regex)) {
    fields[match[1]] = match[2].trim();
  }

  return fields;
}
function valoresOtrasCapas(event) {
  capasDetectadas = {};
  // Clear the current content of the infoBox
  while (infoBox.firstChild) {
    infoBox.removeChild(infoBox.firstChild);
  }

  if (selectedFeature !== null) {
    selectedFeature = null;
    selectedLayer = null;
  }

  mapa.forEachFeatureAtPixel(
    event.pixel,
    (feature, layer) => {
      if (layerFilter(layer)) {
        processFeature(feature, layer, event);
      }
    },
    { hitTolerance: 10 }
  );

  if (Object.keys(capasDetectadas).length > 0) {
    infoBox.style.display = "block";
  } else {
    infoBox.style.display = "none";
  }
}

function addInfoBoxContent(title, content, capaInfoBox) {
  // Create a container for each content block
  const contentContainer = document.createElement("div");

  // Create and add the title
  const titleElement = document.createElement("h6");
  titleElement.textContent = title;
  contentContainer.appendChild(titleElement);

  // Create and add the content
  if (content) {
    const contentElement = document.createElement("p");
    contentElement.innerHTML = content; // Use innerHTML if content includes HTML, otherwise use textContent
    contentContainer.appendChild(contentElement);
  }

  // Add a separator if there's already content in the infoBox
  if (infoBox.childNodes.length > 0) {
    const hr = document.createElement("hr");
    hr.className = "dashed";
    contentContainer.insertBefore(hr, titleElement);
  }

  // Set data attributes on the content container
  contentContainer.dataset.title = title;
  contentContainer.dataset.capaInfoBox = capaInfoBox;

  // Append the content container to the infoBox
  infoBox.appendChild(contentContainer);

  // Get all content containers within the infoBox
  // const contentContainers = infoBox.children;

  // for (const container of contentContainers) {
  //   const title = container.dataset.title;
  //   const capaInfoBox = container.dataset.capaInfoBox;

  //   console.log("Title:", title);
  //   console.log("capaInfoBox:", capaInfoBox);
  // }
}

function processFeature(feature, layer, evento) {
  if (!(layer.getProperties().name in capasDetectadas)) {
    capasDetectadas[layer.getProperties().name] = {
      selected: feature,
      capaObj: layer,
    };

    selectedFeature = feature;
    selectedLayer = layer;
    const featureType =
      layer.getProperties().tipo == "ríos"
        ? "ríos"
        : layer.getProperties().name;

    switch (featureType) {
      case "ríos":
        const nombreRío = feature.get("NOMBRE_TOP");
        //console.log(nombreRío, ":", nombreRío.trim().length);
        if (nombreRío.trim().length > 0) {
          addInfoBoxContent(nombreRío, "", featureType);
        } else {
          delete capasDetectadas[layer.getProperties().name];
        }

        break;
      case "Áreas protegidas":
        const catMa = feature.get("CatMa");
        const nombreAP = feature.get("Nombre");
        addInfoBoxContent(catMa, nombreAP, featureType);
        break;

      case "Líneas de transmisión":
        const lineName = feature.get("Name");
        const faseElec = feature.get("Fase");
        addInfoBoxContent(
          "Línea de transmisión eléctrica",
          `<strong>${lineName}</strong><br/>${faseElec}`,
          featureType
        );
        break;

      case "Corredores naturales":
        addInfoBoxContent(feature.get("nombre"), "");

        break;

      case "Distritos":
        const distrito = feature.get("distrito");
        const provincia = feature.get("LMPR_NOMB");

        // **TODO**: Posibilidad de medir deforestación en objetos (polígonos) de capas.
        // Actualmente no funciona por la naturaleza de pmtiles - los renderfeatures de los polígonos no son consistentes o los rings no se cierran
        // habría que ubicar el polígono completo de otro archivo, con base en un punto donde se hizo clic, por ejemplo
        //

        // if (evento.type == "singleclick") {
        //   console.log("coordenadas: ", evento.coordinate);
        //   if (midiendoArea || midiendoDist) {
        //     console.log("singleclick en distrito pero midiendo");
        //   } else {
        //     console.log("singleclick en distrito");
        //     console.log(feature);

        //     createMeasureTooltip();

        //     var geomGeoJSON = crearPoligonoDeFlat(feature.flatCoordinates_);
        //     //measureTooltipElement.innerHTML = "";

        //     //createMeasureTooltip();
        //     calculoDeforestación(
        //       geomGeoJSON,
        //       feature.properties_.distrito,
        //       evento.coordinate
        //     );
        //   }
        // }

        addInfoBoxContent(`Distrito de ${distrito}`, provincia, featureType);

        break;

      case "Corregimientos":
        const corregimiento = feature.get("LMCO_NOMB");

        addInfoBoxContent(corregimiento, "", featureType);
        break;

      case "Minería y eléctricidad":
        const tipo = feature.get("tipo");
        const subtipo = feature.get("subtipo");
        const nombre = feature.get("NOMBRE");
        const fase = feature.get("FASE");
        const empresa = feature.get("EMPRESA");
        const matriz = feature.get("MATRIZ");

        if (tipo == "generación") {
          const capacidad = feature.get("CAPACID_MW");
          addInfoBoxContent(
            `Planta ${subtipo}<br/>(${capacidad} MW)`,
            `${nombre}<br/>${fase}<br/>${empresa} (${matriz})`,
            featureType
          );
        } else {
          addInfoBoxContent(
            `Mina <br/>(${subtipo})<br/>${nombre}`,
            `${fase}<br/>${empresa} (${matriz})`,
            featureType
          );
        }

        break;
      case "Minería":
      case "Minería":
        var tipoArea = feature.get("tipoarea");
        var noref = feature.get("noref");
        var tipoConcesion = feature.get("tipo");
        tipoConcesion = tipoConcesion ? tipoConcesion + "<br>" : "";
        var empresaMina = feature.get("empresa");
        var mineral = feature.get("mineral").toLowerCase();
        var ubicacion = feature.get("ubicacion");
        ubicacion = ubicacion ? ubicacion + "<br>" : "";

        var estatus = feature.get("estado");
        estatus = estatus ? "<br/><small>" + estatus + "</small>" : "";

        var minaLinea1 = empresaMina ? `${empresaMina}` : `${tipoArea}`;

        var minaLinea2 = empresaMina
          ? `<strong>${tipoArea}, ${mineral}</strong><br/>(Ref: ${noref})<br/>${tipoConcesion}${ubicacion}${estatus}`
          : `(Ref: ${noref})`;

        addInfoBoxContent(minaLinea1, minaLinea2, featureType);
        break;
      // Add cases for other feature types...

      default:
        break;
    }
  }
}

function layerFilter(layer) {
  const layerFilters = [
    capaMinaHidro,
    capaTransmisiónEléctrica,
    capaCorredoresNaturales,
    capaSINAP,
    capaDistritos,
    capaMineria,
    capaCorregimientos,
  ];
  if (layer) {
    const layerType = layer.getProperties().tipo;
    return layerFilters.includes(layer) || layerType === "ríos";
  } else {
    return false;
  }
}

function handleMineria(feature) {
  console.log("feature mina: ");
  const tipoMina = feature.get("tipo");
  const subtipo = feature.get("subtipo");
  const nombreMina = feature.get("NOMBRE");
  const fase = feature.get("FASE");
  const empresa = feature.get("EMPRESA");
  const matriz = feature.get("MATRIZ");

  if (tipoMina === "generación") {
    const capacidad = feature.get("CAPACID_MW");
    addInfoBoxContent(
      `Planta ${subtipo}<br/>(${capacidad} MW)`,
      `${nombreMina}<br/>${fase}<br/>${empresa} (${matriz})`
    );
  } else {
    addInfoBoxContent(
      `Mina <br/>(${subtipo})<br/>${nombreMina}`,
      `${fase}<br/>${empresa} (${matriz})`
    );
  }
}

// function valoresOtrasCapas(event) {
//   var no = 0;
//   var capasDetectadas = {};
//   infoBoxContent = "";

//   if (selected !== null) {
//     selected = null;
//   }

//   mapa.forEachFeatureAtPixel(
//     event.pixel,
//     function (feature, layer) {
//       if (!(layer.getProperties().name in capasDetectadas)) {
//         capasDetectadas[layer.getProperties().name] = {
//           selected: feature,
//           capa: layer,
//         };
//       }
//       selected = feature;
//       capa = layer;
//     },
//     {
//       layerFilter: function (layer) {
//         return (
//           [
//             capaMinaHidro,
//             capaTransmisiónEléctrica,
//             capaSINAP,
//             capaDistritos,
//             capaCorregimientos,
//           ].includes(layer) || layer.getProperties().tipo == "ríos"
//         );
//       },
//       hitTolerance: 10,
//     }
//   );

//   if (Object.keys(capasDetectadas).length > 0) {
//     Object.keys(capasDetectadas).forEach((nombreCapa) => {
//       if (nombreCapa == "SINAP") {
//         const tipo = capasDetectadas[nombreCapa].selected.get("CatMa");
//         let nombre;
//         nombre = capasDetectadas[nombreCapa].selected.get("Nombre");
//         if (infoBoxContent.length > 0) {
//           infoBoxContent += "<hr class='dashed'>";
//         }
//         infoBoxContent += `<h6>${tipo}</h6><br/>${nombre}`;
//       }
//       //else
//       if (nombreCapa == "Líneas de transmisión") {
//         const nombre = capasDetectadas[nombreCapa].selected.get("Name");
//         const fase = capasDetectadas[nombreCapa].selected.get("Fase");

//         if (infoBoxContent.length > 0) {
//           infoBoxContent += "<hr class='dashed'>";
//         }

//         infoBoxContent += `<h6>Línea de transmisión eléctrica </h6><br/><strong>${nombre}</strong><br/>${fase}`;
//       }
//       //else
//       if (nombreCapa == "Distritos") {
//         const distrito = capasDetectadas[nombreCapa].selected.get("distrito");
//         const provincia = capasDetectadas[nombreCapa].selected.get("LMPR_NOMB");
//         if (infoBoxContent.length > 0) {
//           infoBoxContent += "<hr class='dashed'>";
//         }
//         infoBoxContent += `<h6>Distrito de ${distrito} </h6><br/>${provincia}`;
//       }

//       if (nombreCapa == "Corregimientos") {
//         const corregimiento =
//           capasDetectadas[nombreCapa].selected.get("LMCO_NOMB");

//         if (infoBoxContent.length > 0) {
//           infoBoxContent += "<hr class='dashed'>";
//         }
//         infoBoxContent += `<h6>${corregimiento} </h6>`;
//       }
//       //else
//       if (nombreCapa == "Minería y eléctricidad") {
//         const tipo = capasDetectadas[nombreCapa].selected.get("tipo");
//         const subtipo = capasDetectadas[nombreCapa].selected.get("subtipo");
//         const nombre = capasDetectadas[nombreCapa].selected.get("NOMBRE");
//         const fase = capasDetectadas[nombreCapa].selected.get("FASE");
//         const empresa = capasDetectadas[nombreCapa].selected.get("EMPRESA");
//         const matriz = capasDetectadas[nombreCapa].selected.get("MATRIZ");

//         if (tipo == "generación") {
//           const capacidad =
//             capasDetectadas[nombreCapa].selected.get("CAPACID_MW");
//           if (infoBoxContent.length > 0) {
//             infoBoxContent += "<hr class='dashed'>";
//           }
//           infoBoxContent += `<h6>Planta ${subtipo}<br/>(${capacidad} MW)</h6><br/><h6>${nombre}</h6><br/>${fase}<br/>${empresa} (${matriz})`;
//         } else {
//           if (infoBoxContent.length > 0) {
//             infoBoxContent += "<hr class='dashed'>";
//           }
//           infoBoxContent += `<h6>Mina <br/>(${subtipo})</h6><br/><h6>${nombre}</h6><br/>${fase}<br/>${empresa} (${matriz})`;
//         }
//       }
//     });

//     infoBox.innerHTML = infoBoxContent;
//     infoBox.style.display = "block";
//   } else {
//     infoBoxContent = "";
//     capasDetectadas = [];
//     infoBox.innerHTML = "&nbsp;";
//     infoBox.style.display = "none";
//   }
// }

function alternarCapas(capaCambio = false, tipo = "pérdida") {
  // Makes sure only one of these layers is visible at a time and sets slider range
  // Runs on slider initialization (with no arguments) or on layer visibility change

  let unaSolaVisible = false;

  const botonColorElegido = Array.from(botonesColor).find(
    (radio) => radio.checked
  );

  function setSliderOptions(capaCambio) {
    const fuenteMax = capaCambio.getSource().sourceInfo_[0].max;
    const fuenteMin = capaCambio.getSource().sourceInfo_[0].min;
    const nombreCapa = capaCambio.getProperties().name;
    const estadoMin = estadoCapasPérdida[nombreCapa].min;
    const estadoMax = estadoCapasPérdida[nombreCapa].max;

    // console.log(
    //   "Voy a hacer el set, esta la capaCambio: ",
    //   nombreCapa,
    //   "Min y max: ",
    //   estadoMin,
    //   estadoMax
    // );

    sliderAñoPérdida.noUiSlider.updateOptions({
      range: {
        min: fuenteMin,
        max: fuenteMax,
      },
      start: [estadoMin, estadoMax],
    });
  }

  function processLayer(li) {
    const nombreCapa = li.getProperties().name;
    if (capaCambio) {
      if (capaCambio !== li && li.values_.tipo === tipo && li.getVisible()) {
        li.setVisible(false);
      }
    } else {
      // This one runs on initialization, i.e. no arguments
      // Create the initial state of the two layers

      if (li.values_.tipo === tipo) {
        //console.log("capa: ", li.getProperties().name);
        const { maxInicial, minInicial } = li.getSource().sourceInfo_[0];
        estadoCapasPérdida[nombreCapa] = {
          min: minInicial,
          max: maxInicial,
          color: botonColorElegido.value,
        };

        if (unaSolaVisible && li.getVisible()) {
          // Only one of these layers should be visible at a time
          li.setVisible(false);
        } else if (li.getVisible()) {
          unaSolaVisible = true;
          sliderAñoPérdida.noUiSlider.enable();
          setSliderOptions(li);
          // console.log(
          //   "creo que es la primera vez y voy a asignar actual capa a esta: ",
          //   li
          // );

          actualCapaPérdida = li;
        }
      }
    }
  }

  if (capaCambio && capaCambio.getVisible()) {
    sliderAñoPérdida.noUiSlider.enable();
    actualCapaPérdida = capaCambio;
    setSliderOptions(capaCambio);
  } else {
    //console.log("creo que es la primera vez");
    sliderAñoPérdida.noUiSlider.disable();
    actualCapaPérdida = null;
  }

  mapa.getLayers().forEach(processLayer);
}

function identificarCosas(event) {
  if (event.dragging) {
    return;
  }

  valoresOtrasCapas(event);

  // if (!capaTerritorios.state_.visible) {
  //   valoresOtrasCapas(event);
  // } else {
  //   const viewResolution = /** @type {number} */ (
  //     mapa.getView().getResolution()
  //   );
  //   const url = territoriosWMSSource.getFeatureInfoUrl(
  //     event.coordinate,
  //     viewResolution,
  //     "EPSG:3857",
  //     { INFO_FORMAT: "text/plain" }
  //   );

  //   if (!url) {
  //     valoresOtrasCapas(event);
  //     return;
  //   } else {
  //     fetch(url)
  //       .then((response) => response.text())
  //       .then((textoWMS) => {
  //         if (!textoWMS.includes("no features were found", 0)) {
  //           var capaTerr = parseWMSString(textoWMS);

  //           var tipo = "Territorio indígena";

  //           var nombreCompleto =
  //             capaTerr.cat == "Comarca"
  //               ? `Comarca ${capaTerr.nombre}`
  //               : `${capaTerr.nombre} (${capaTerr.cat})`;

  //           infoBox.innerHTML = `<h6> ${tipo} </h6><br/><br/> ${nombreCompleto}`;
  //           infoBox.style.display = "block";
  //         } else {
  //           valoresOtrasCapas(event);
  //         }
  //       });
  //   }
  // }

  // Para las mediciones

  if ((midiendoArea || midiendoDist) && sketch) {
    const geom = sketch.getGeometry();
    if (geom instanceof Polygon) {
      // helpMsg = continuePolygonMsg;
      helpTooltipElement.classList.add("hidden");
    } else if (geom instanceof LineString) {
      // helpMsg = continueLineMsg;
      helpTooltipElement.classList.add("hidden");
    }
  } else if (midiendoArea || midiendoDist) {
    helpTooltipElement.classList.remove("hidden");

    helpTooltip.setPosition(event.coordinate);
  }
}

// Poner blend modes a capas de conectividad

function ponerModoBlend(blendMode) {
  const connectivityLayers = document.querySelectorAll(".capaConectividad");
  connectivityLayers.forEach((layer) => {
    layer.style.mixBlendMode = blendMode;
  });
}

function handleFullscreen() {
  if (document.fullscreen || document.webkitFullscreenElement) {
    toggleBtn.classList.add("on");
    toggleBtn.setAttribute("aria-label", "Salir de pantalla completa");
  } else {
    toggleBtn.classList.remove("on");
    toggleBtn.setAttribute("aria-label", "Cambiar a pantalla completa");
  }
}

// FUENTE ARCGIS PARA RIOS

function URLArcGISWFS(extent, capaRíos) {
  return (
    "https://dservices8.arcgis.com/UYEK9SUzH1am9mbk/arcgis/services/Rios/WFSServer?service=wfs&" +
    "version=2.0.0&request=GetFeature&typename=" +
    capaRíos +
    "&" +
    "outputFormat=GEOJSON&srsname=EPSG:3857&" +
    "bbox=" +
    extent.join(",") +
    ",EPSG:3857"
  );
}

// FUNCIONES DE ESTILO

function anchoRios(feature, featureColumn, resolution) {
  const scheidegger = parseFloat(feature.get(featureColumn));
  return new Style({
    stroke: new Stroke({
      // color: "#adcff3",
      color: "#fff",

      width:
        estiloRiosScheidegger.find(graduatedCallback, scheidegger)[2] /
        resolution,
    }),
  });
}

// Esta es para el estilo de los ríos
// Toma un array de estilos con rangos (scheidegger en este caso) y elige uno

function graduatedCallback(styleTable) {
  return this >= styleTable[0] && this < styleTable[1];
}

// Esta función pone espacios entre letras, usando un
// caracter Unicode

function ponerEspacios(label, espaciado) {
  // &#8202 es un espacio chiquitito de Unicode
  var espacios = "\u200A".repeat(espaciado);
  return label.split("").join(espacios);
}

function featureTextoEstilo(feature, resolution) {
  const label = feature.get("Nombre");
  const espaciado = feature.get("espaciado") || 2;
  let tamaño = feature.get("tamano") || 6;

  tamaño = (tamaño * 250) / resolution;

  const fuente = `${tamaño}px Delicious-sc`;

  const labelConEspacios = ponerEspacios(label, espaciado);

  estiloFeaturesLineal.getText().setText(labelConEspacios);
  estiloFeaturesLineal.getText().setFont(fuente);

  return [estiloFeaturesLineal];
}

function estiloPolyMineria(feature, resolution) {
  const tipoarea = feature.get("tipoarea");
  const colour =
    tipoarea in mineriaTipo ? mineriaTipo[tipoarea] : mineriaTipo["else"];
  return new Style({
    fill: new Fill({
      color: colour[0],
    }),
    stroke: new Stroke({
      color: colour[1],
      width: 2,
      lineJoin: "miter",
    }),
  });
}

function estiloTransmisiónEléctrica(feature, resolution) {
  const fase = feature.get("Fase");

  var color = fase == "en proyecto" ? "#d2b837" : "#222";

  return [
    new Style({
      stroke: new Stroke({
        // color: "#adcff3",
        color: color,
        lineDash: [4, 4],
        width: 4,
        lineCap: "butt",
      }),
    }),
  ];
}

function actualizarEstadoPérdida(cambio) {
  if (actualCapaPérdida) {
    var nombreCapa = actualCapaPérdida.getProperties().name;
    var rangoAños = sliderAñoPérdida.noUiSlider.get();
    var elegidoMin = parseInt(rangoAños[0]);
    var elegidoMax = parseInt(rangoAños[1]);

    // ajuste is added on layer source when actual pixel values are different from slider values
    // i.e. Hansen has 19 to mean 2019
    // var ajuste = actualCapaPérdida.getSource().sourceInfo_[0].ajuste || 0;
    // var mínimo = mínimo || elegidoMin + ajuste;
    // var máximo = máximo || elegidoMax + ajuste;

    if (
      cambio == "rango" &&
      estadoCapasPérdida[nombreCapa].min == elegidoMin &&
      estadoCapasPérdida[nombreCapa].max == elegidoMax
    ) {
      console.log("rango sin cambios");
      return false;
    }
    var botonColorElegido = Array.from(botonesColor).find(
      (radio) => radio.checked
    );
    var colorElegido = colorElegido || botonColorElegido.value;

    //capa = actualCapaPérdida;
    //ajuste = actualCapaPérdida.getSource().sourceInfo_[0].ajuste || 0;

    estadoCapasPérdida[nombreCapa] = {
      min: elegidoMin,
      max: elegidoMax,
      color: colorElegido,
    };
    return true;
  }
}

function ajustarEstiloPérdida(
  colorElegido = false,
  mínimo = false,
  máximo = false,
  capaPérdida = false
) {
  capaPérdida = actualCapaPérdida;

  var fuente = actualCapaPérdida.getSource().sourceInfo_[0];

  if (capaPérdida) {
    var botonColorElegido = Array.from(botonesColor).find(
      (radio) => radio.checked
    );
    var estiloColores = [
      "interpolate",
      ["linear"],
      ["band", 1],
      0,
      "#1a4dda00",
    ];

    var ajuste = fuente.ajuste || 0;

    var rangoAños = sliderAñoPérdida.noUiSlider.get();

    var elegidoMin = parseInt(rangoAños[0]);
    var elegidoMax = parseInt(rangoAños[1]);

    colorElegido = colorElegido || botonColorElegido.value;

    mínimo = mínimo || elegidoMin + ajuste;
    máximo = máximo || elegidoMax + ajuste;

    var paletaElegida = botonColorElegido.dataset.paleta || false;

    if (paletaElegida) {
      var pasos = máximo - mínimo + 1 < 2 ? 2 : máximo - mínimo + 1;

      var coloritos = [
        mínimo - 1,
        "#1a4dda00",
        ...getColorStops(paletaElegida, mínimo, máximo, pasos, false),
      ];
      estiloColores = estiloColores.concat(coloritos);

      var coloresFondoSlider = colormap({
        colormap: paletaElegida,
        nshades: 2,
        format: "hex",
      });

      fondoSlider.style.background = "none";
      fondoSlider.style.backgroundImage =
        "linear-gradient(to right, " +
        coloresFondoSlider[0] +
        ", " +
        coloresFondoSlider[1] +
        ")";
    } else if (colorElegido == "aleatorio") {
      estiloColores.push(mínimo - 1, "#1a4dda00");

      var porcentajes = Math.round(1000 / (máximo - mínimo + 1)) / 10;
      var coloritosSlider = [];
      var unColor = "";
      var parada = 1;
      var rango = fuente.max - fuente.min;
      var ancho = parseFloat(getComputedStyle(fondoSlider).width);

      for (let i = elegidoMin; i < elegidoMax + 1; i++) {
        //    console.log("i: ", i - maxRangoAños[0]);
        estiloColores.push(i + ajuste, coloresAleatorios[i - maxRangoAños[0]]);

        coloritosSlider.push(
          unColor.concat(
            coloresAleatorios[i - maxRangoAños[0]],
            " ",
            porcentajes * parada - porcentajes,
            "%, ",
            coloresAleatorios[i - maxRangoAños[0]],
            " ",
            porcentajes * parada,
            "%"
          )
        );

        parada++;
      }

      var gradienteTiritas = coloritosSlider.join(", ");

      // Put the pallete on the slider background for reference

      fondoSlider.style.background = "none";
      fondoSlider.style.backgroundImage =
        "linear-gradient(to right, " + gradienteTiritas + ")";

      // But... we need to set colors in the middle of the ticks, so all of the below

      // aquí queremos jugar con los índices 0 y 4. los demás permanecen igual

      var matrizArray = [0, 0, 0, 1, 0, 0];

      var escala = (máximo - mínimo) / rango;

      var translate = ((mínimo - fuente.min - ajuste) * ancho) / rango;

      var nuevaEscala = escala / (máximo - mínimo) + escala;

      console.log("nuevaEscala", nuevaEscala);

      matrizArray[0] = nuevaEscala;

      // Given the number of ticks, how wide is a year in percentage? So we can apply it to the transform

      var porcentajeAnchoMedida = 1 / (sliderMax - sliderMin);

      var nuevoTranslate = translate - (ancho * porcentajeAnchoMedida) / 2;

      matrizArray[4] = nuevoTranslate;

      var nuevaMatriz = "matrix(" + matrizArray.join(", ") + ")";

      fondoSlider.style.transform = nuevaMatriz;
    } else {
      estiloColores.push(
        mínimo - 1,
        "#1a4dda00",
        mínimo,
        colorElegido,
        máximo,
        colorElegido
      );

      fondoSlider.style.background = colorElegido;

      //    colorFondoControlVolumen(sliderAñoPérdida, colorElegido);
    }

    estiloColores.push(máximo + 1, "#1a4dda00");

    var estilo = {
      color: estiloColores,
    };

    if (capaPérdida) {
      // debugger;
      capaPérdida.setStyle(estilo);
      capaPérdida.changed();
    } else {
      capaHansen.setStyle(estilo);
      capaHansen.changed();
    }
  }
  actualizarEstadoPérdida();
  return estilo;
}

function hacerPaleta(minConect, maxConect, color1, color2) {
  //var paleta = ["interpolate", ["linear"], conectividad];

  var rango = maxConect - minConect;

  var coloritos = [
    minConect,
    color1.concat(1),
    maxConect - rango / 3,
    color2.concat(1),
    maxConect,
    color2.concat(1),
    maxConect + 1,
    [255, 255, 255, 0],
  ];

  // var nuevoestilo = {
  //   color: paleta.concat(colores),
  // };

  //return nuevoestilo;
  return coloritos;
}

// Create functions to extract KML and icons from KMZ array buffer,
// which must be done synchronously.

// function getKMLData(buffer) {
//   console.log("GET KML!!");
//   let kmlData;
//   zip.load(buffer);
//   console.log(zip);
//   const kmlFile = zip.file(/\.kml$/i)[0];

//   if (kmlFile) {
//     console.log("leyendo archivo");
//     kmlData = kmlFile.asText();
//   }
//   console.log(kmlFile);

//   return kmlData;
// }

// function getKMLImage(href) {
//   const index = window.location.href.lastIndexOf("/");
//   if (index !== -1) {
//     const kmlFile = zip.file(href.slice(index + 1));
//     if (kmlFile) {
//       return URL.createObjectURL(new Blob([kmlFile.asArrayBuffer()]));
//     }
//   }
//   return href;
// }

// Function to show the modal
function showAlertModal(message) {
  const modal = document.getElementById("alertModal");
  const modalContent = modal.querySelector(".modal-content");
  const closeBtn = modal.querySelector(".cerrarModal");

  // Set the message
  modalContent.querySelector("p").textContent = message;

  // Display the modal
  modal.style.display = "block";

  // Fade in the modal
  setTimeout(function () {
    modal.style.opacity = "1";
  }, 50); // Give a slight delay to start the transition

  // Close the modal after 5 seconds
  setTimeout(function () {
    modal.style.opacity = "0";
    setTimeout(function () {
      modal.style.display = "none";
    }, 300); // Wait for the transition to complete before hiding the modal
  }, 3000);

  // Close the modal when close button is clicked
  closeBtn.onclick = function () {
    modal.style.display = "none";
  };
}
